import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, BarChart3, Bell, Box, Briefcase, CheckCircle2,
  ChevronDown, Circle, Clock3, Compass, Database, DollarSign, Download,
  FileSearch, Filter, Gauge, Layers, Lightbulb, LogOut, Map,
  Package, PauseCircle, Plus, RotateCcw, Search, SlidersHorizontal, Sparkles,
  Target, Trash2, TrendingUp, Users, XCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogTitle,
} from "../components/ui/dialog";
import {
  adminLogout, adminMe, getMyRelease,
  listSelections, createSelection, updateSelection, deleteSelection,
} from "../lib/adminApi";
import useProductLogo from "../lib/useProductLogo";

// ---------------------------------------------------------------------------
// Style tokens (McKinsey / SAP Fiori consulting palette)
// ---------------------------------------------------------------------------
const C = {
  blue: "#025979",
  blueSoft: "#E0F2FE",
  green: "#16A34A",
  greenSoft: "#DCFCE7",
  amber: "#F59E0B",
  amberSoft: "#FEF3C7",
  red: "#DC2626",
  redSoft: "#FEE2E2",
  slate: "#0F172A",
  muted: "#64748B",
};

const STATUS_META = {
  planning: { label: "Planning", bg: C.blueSoft, fg: C.blue, dot: C.blue },
  in_progress: { label: "In Progress", bg: C.amberSoft, fg: "#92400E", dot: C.amber },
  paused: { label: "Paused", bg: "#F1F5F9", fg: C.slate, dot: C.muted },
  completed: { label: "Completed", bg: C.greenSoft, fg: "#166534", dot: C.green },
};

const RISK_META = {
  low: { label: "Low risk", bg: C.greenSoft, fg: "#166534" },
  medium: { label: "Medium risk", bg: C.amberSoft, fg: "#92400E" },
  high: { label: "High risk", bg: C.redSoft, fg: "#991B1B" },
};

const SEVERITY_META = {
  high: { bg: C.redSoft, fg: "#991B1B", label: "High" },
  medium: { bg: C.amberSoft, fg: "#92400E", label: "Medium" },
  low: { bg: C.blueSoft, fg: C.blue, label: "Informational" },
};

const STAGE_META = {
  selected: { label: "Selected", bg: "#F1F5F9", fg: "#475569", dot: "#94A3B8" },
  under_review: { label: "Under Review", bg: "#DBEAFE", fg: "#1E40AF", dot: "#3B82F6" },
  commercials: { label: "In Commercials", bg: "#FFEDD5", fg: "#9A3412", dot: "#F97316" },
  approved: { label: "Approved", bg: "#D1FAE5", fg: "#166534", dot: "#16A34A" },
  on_hold: { label: "On Hold", bg: "#FEF3C7", fg: "#92400E", dot: "#F59E0B" },
  rejected: { label: "Rejected", bg: "#FEE2E2", fg: "#991B1B", dot: "#DC2626" },
};

const TABS = [
  {
    id: "program",
    label: "Transformation Program",
    subtitle:
      "Transforming warehouse operations end-to-end to drive efficiency and measurable impact",
    icon: Target,
  },
  { id: "selections", label: "Initiatives", icon: Layers },
  { id: "recommendations", label: "Recommendations", icon: Sparkles },
  { id: "insights", label: "Insights", icon: Compass },
];

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------
const fmtMoney = (v, currency = "USD") => {
  const n = Number(v || 0);
  if (!Number.isFinite(n) || n === 0) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency", currency, maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `$${Math.round(n).toLocaleString()}`;
  }
};
const fmtNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n).toLocaleString() : "—";
};
const fmtPct = (v) => (v == null || v === "" ? "—" : `${Math.round(Number(v))}%`);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
// Convert raw snake_case / SCREAMING_SNAKE values into clean Title Case so
// data captured during the assessment renders in business-friendly form.
const humanize = (v) => {
  if (v == null) return "";
  const s = String(v).trim();
  if (!s) return "";
  return s
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b([a-zA-Z])([a-zA-Z]*)/g, (_, h, t) => h.toUpperCase() + t.toLowerCase());
};

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------
const Chip = ({ children, bg, fg, testid }) => (
  <span
    data-testid={testid}
    className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[11px] font-semibold"
    style={{ backgroundColor: bg, color: fg }}
  >
    {children}
  </span>
);

const Card = ({ children, className = "", ...rest }) => (
  <div
    className={`bg-white border border-[#E5E7EB] rounded-sm ${className}`}
    {...rest}
  >
    {children}
  </div>
);

const SectionHeader = ({ icon: Icon, title, hint }) => (
  <div className="flex items-start gap-2 mb-2.5">
    {Icon && (
      <div
        className="flex items-center justify-center w-6 h-6 rounded-sm shrink-0"
        style={{ backgroundColor: C.blueSoft, color: C.blue }}
      >
        <Icon className="w-3 h-3" strokeWidth={2.25} />
      </div>
    )}
    <div>
      <h3
        className="text-[13px] font-bold text-[#0F172A] tracking-tight leading-tight"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        {title}
      </h3>
      {hint && <p className="text-[10.5px] text-[#64748B] mt-0.5">{hint}</p>}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Realized ROI history — tiny client-side snapshot store keyed by assessment.
// We keep this on the browser to satisfy "minimal backend lift": every time
// the rollup recomputes, the current ISO-week value is upserted, and the
// most recent *prior* week's value is used to render a delta pill in the
// Program Progress card. No server roundtrip, no schema change.
// ---------------------------------------------------------------------------
function getIsoWeek(date = new Date()) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

function loadRoiHistory(assessmentId) {
  if (!assessmentId || typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(`roi_realized_history:${assessmentId}`) || "{}");
  } catch {
    return {};
  }
}

function saveRoiHistory(assessmentId, history) {
  if (!assessmentId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`roi_realized_history:${assessmentId}`, JSON.stringify(history));
  } catch {
    /* quota / private-mode — silently ignore */
  }
}

const KpiTile = ({ label, value, hint, color = C.blue, bg = C.blueSoft, testid }) => (
  <Card className="p-5" data-testid={testid}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#64748B]">
          {label}
        </p>
        <p
          className="mt-2 text-2xl font-bold text-[#0F172A]"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {value}
        </p>
        {hint && <p className="mt-1 text-xs text-[#64748B]">{hint}</p>}
      </div>
      <div
        className="shrink-0 w-8 h-8 rounded-sm flex items-center justify-center"
        style={{ backgroundColor: bg, color }}
      >
        <TrendingUp className="w-4 h-4" strokeWidth={2.25} />
      </div>
    </div>
  </Card>
);

// ---------------------------------------------------------------------------
// Empty state + not-authorized
// ---------------------------------------------------------------------------
const EmptyState = ({ onBack }) => (
  <Card className="py-16 px-8 text-center max-w-xl mx-auto" data-testid="client-dashboard-empty">
    <div
      className="mx-auto w-12 h-12 rounded-sm flex items-center justify-center"
      style={{ backgroundColor: C.blueSoft, color: C.blue }}
    >
      <Clock3 className="w-6 h-6" strokeWidth={2.25} />
    </div>
    <h2
      className="mt-5 text-2xl font-bold text-[#0F172A] tracking-tight"
      style={{ fontFamily: "Chivo, sans-serif" }}
      data-testid="client-dashboard-empty-title"
    >
      No transformation program available
    </h2>
    <p className="mt-3 text-sm text-[#4B5563] leading-relaxed max-w-md mx-auto">
      Your consultant has not yet shared a transformation program with this
      account.
    </p>
    <Button
      data-testid="client-dashboard-empty-back-btn"
      onClick={onBack}
      variant="outline"
      className="mt-8 rounded-sm h-9 px-4 text-sm font-semibold border-[#E5E7EB] text-[#0F172A] hover:bg-[#F1F5F9]"
    >
      <ArrowLeft className="w-4 h-4 mr-1.5" />
      Back to Portal
    </Button>
  </Card>
);

// ---------------------------------------------------------------------------
// Tab 1 — Transformation Program  (Execution Layer)
//
// Layered rendering based on where the client sits in the lifecycle:
//   • NO selections yet       → Strategy Layer summary (ROI/strategy) +
//                                guidance to start selecting initiatives.
//   • Selections exist but    → Decision pipeline (kanban-style totals) +
//     none approved yet        selected-initiative table.
//   • Approved selections     → Full execution layer: approved-initiative
//                                table, execution status, progress, timeline.
// ---------------------------------------------------------------------------
const STAGE_ORDER = ["selected", "under_review", "commercials", "approved"];

// ---------------------------------------------------------------------------
// Phase-5 — Client Dashboard alignment blocks (mirror Consultant Recs tab)
// ---------------------------------------------------------------------------
function clientPrettyWaveName(wave, index) {
  const i = (index ?? 0) + 1;
  const raw = String(wave?.name || "").toLowerCase();
  if (i === 1 || raw.includes("pilot")) return `Wave ${i} (Pilot)`;
  return `Wave ${i} (Rollout Wave ${i - 1})`;
}

function ClientPhasedRoiCard({ roi }) {
  const sby = roi.savings_by_year || {};
  const currency = roi.currency || "USD";
  const fmt = (n) => fmtMoney(n, currency);
  const cell = (label, val, testid) => (
    <div
      data-testid={testid}
      className="bg-white border border-[#E5E7EB] rounded-sm px-3 py-2"
    >
      <p className="text-[10px] uppercase tracking-[0.12em] text-[#64748B]">{label}</p>
      <p
        className="text-base font-bold text-[#0F172A] mt-1"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        {val}
      </p>
    </div>
  );
  return (
    <Card className="p-5" data-testid="client-phased-roi-card">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#025979]">
            Phased ROI Realization
          </p>
          <p className="text-[11px] text-[#64748B] mt-0.5">
            Savings realized progressively as warehouses go live.
          </p>
        </div>
        <p className="text-[11px] text-[#475569]">
          Phased payback:{" "}
          <span
            className="font-semibold text-[#0F172A]"
            data-testid="client-phased-payback"
          >
            {roi.phased_payback_months != null
              ? `${Number(roi.phased_payback_months).toFixed(1)} mo`
              : "—"}
          </span>
        </p>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {cell("Year 1", fmt(sby.year1), "client-phased-y1")}
        {cell("Year 2", fmt(sby.year2), "client-phased-y2")}
        {cell("Year 3", fmt(sby.year3), "client-phased-y3")}
      </div>
    </Card>
  );
}

function ClientInvestmentBreakdown({ costBreakdown }) {
  const cb = costBreakdown || {};
  const ot = cb.one_time || {};
  const rc = cb.recurring || {};
  const currency = cb.currency || "USD";
  const fmt = (n) => fmtMoney(n, currency);
  const totalOT = Number(ot.total_one_time_investment || 0);
  const pct = (n) =>
    totalOT > 0 ? `${((Number(n || 0) / totalOT) * 100).toFixed(0)}%` : "—";
  const hasAnyValue =
    Number(ot.implementation_fees || 0) > 0
    || Number(ot.hardware_devices || 0) > 0
    || Number(ot.integration_custom_dev || 0) > 0
    || Number(ot.internal_business_effort || 0) > 0
    || Number(rc.sap_license_subscription_delta || 0) > 0
    || Number(rc.annual_support_ams || 0) > 0;
  if (!hasAnyValue) return null;

  const capexRow = (label, value, share, testid) => (
    <div
      className="flex items-baseline justify-between py-1 gap-3"
      data-testid={testid}
    >
      <span className="text-[11px] text-[#64748B] min-w-0 truncate">{label}</span>
      <span className="flex items-baseline gap-2 whitespace-nowrap">
        <span className="text-[10px] text-[#94A3B8] tabular-nums">{share}</span>
        <span className="text-[12px] font-semibold text-[#0F172A] tabular-nums">
          {value}
        </span>
      </span>
    </div>
  );
  const opexRow = (label, value, testid) => (
    <div
      className="flex items-baseline justify-between py-1"
      data-testid={testid}
    >
      <span className="text-[11px] text-[#64748B]">{label}</span>
      <span className="text-[12px] font-semibold text-[#0F172A] tabular-nums">
        {value}
      </span>
    </div>
  );
  return (
    <Card className="p-5" data-testid="client-investment-breakdown">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#025979] mb-3">
        Investment Breakdown
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#0F172A] mb-1">
            One-Time Investment (CAPEX)
          </p>
          {capexRow("Implementation Fees", fmt(ot.implementation_fees), pct(ot.implementation_fees), "client-cost-impl-fees")}
          {capexRow("Hardware & Devices", fmt(ot.hardware_devices), pct(ot.hardware_devices), "client-cost-hw")}
          {capexRow("Integration / Custom Dev", fmt(ot.integration_custom_dev), pct(ot.integration_custom_dev), "client-cost-integration")}
          {capexRow("Internal Business Effort", fmt(ot.internal_business_effort), pct(ot.internal_business_effort), "client-cost-internal")}
          <div className="mt-1 pt-1.5 border-t border-[#E5E7EB]">
            <div
              className="flex items-baseline justify-between py-1"
              data-testid="client-cost-total-onetime"
            >
              <span className="text-[11px] font-semibold text-[#0F172A]">
                Total One-Time Investment
              </span>
              <span className="text-[12px] font-bold text-[#025979] tabular-nums">
                {fmt(totalOT)}
              </span>
            </div>
          </div>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#0F172A] mb-1">
            Annual Recurring (OPEX)
          </p>
          {opexRow("SAP License / Subscription Δ", fmt(rc.sap_license_subscription_delta), "client-cost-license")}
          {opexRow("Annual Support / AMS", fmt(rc.annual_support_ams), "client-cost-ams")}
          <div className="mt-1 pt-1.5 border-t border-[#E5E7EB]">
            <div
              className="flex items-baseline justify-between py-1"
              data-testid="client-cost-total-recurring"
            >
              <span className="text-[11px] font-semibold text-[#0F172A]">
                Total Annual Recurring
              </span>
              <span className="text-[12px] font-bold text-[#025979] tabular-nums">
                {fmt(rc.total_annual_recurring)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

function ClientImplementationSummary({ rollout, totalWeeks }) {
  const waves = rollout.waves || [];
  const rolloutLabel =
    ({
      pilot_phased: "Pilot → Phased",
      parallel: "Parallel",
      big_bang: "Big Bang",
    })[rollout.mode] || rollout.mode || "—";
  const months = totalWeeks > 0 ? (totalWeeks / 4.345).toFixed(1) : "0";
  return (
    <Card className="p-5" data-testid="client-impl-summary">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#025979]">
          Implementation Summary
        </p>
        <p className="text-[11px] text-[#64748B]">
          Rollout:{" "}
          <span className="font-semibold text-[#0F172A]" data-testid="client-impl-rollout">
            {rolloutLabel}
          </span>
        </p>
      </div>
      <p className="text-[11px] text-[#475569] mb-2" data-testid="client-impl-duration-line">
        Total duration:{" "}
        <span className="font-semibold text-[#0F172A]">
          {totalWeeks > 0 ? `${Math.round(totalWeeks)} weeks (${months} months)` : "—"}
        </span>
      </p>
      <div className="space-y-1" data-testid="client-impl-waves">
        {waves.map((w, i) => (
          <div
            key={w.id || i}
            data-testid={`client-impl-wave-${i}`}
            className="flex items-center justify-between gap-2 text-[11px] text-[#0F172A] border-l-2 border-[#025979] pl-2 py-0.5"
          >
            <span className="truncate">
              <span className="font-semibold">
                {clientPrettyWaveName(w, i)}
                {w.go_live_month ? ` → Go-live Month ${w.go_live_month}` : ""}
              </span>
              {Array.isArray(w.scope_labels) && w.scope_labels.length > 0 && (
                <span className="text-[#64748B]"> · {w.scope_labels.join(" + ")}</span>
              )}
            </span>
            <span className="text-[#64748B] whitespace-nowrap">
              W{w.start_week ?? 0}–{w.end_week ?? ((w.start_week || 0) + (w.duration_weeks || 0))}
            </span>
          </div>
        ))}
      </div>
      <p
        data-testid="client-impl-insight"
        className="mt-3 pt-2 border-t border-[#E5E7EB] text-[11px] text-[#475569] italic"
      >
        Phased rollout reduces risk and enables early value realization.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Control-tower helpers — purely presentational, derived from existing data.
// No new calculations: maps existing value_drivers / focus_areas / insights /
// selection stages into the new layout primitives.
// ---------------------------------------------------------------------------
const STAGE_PROGRESS_PCT = {
  selected: 25,
  under_review: 50,
  commercials: 75,
  approved: 100,
};

function deriveImpactBuckets(drivers = []) {
  // Map driver `key` strings into canonical impact buckets so the right-panel
  // "Transformation Impact" card has stable headings. Same key heuristic the
  // backend uses in `_program_rationale`.
  const b = { productivity: 0, accuracy: 0, inventory: 0, space: 0, throughput: 0 };
  for (const d of drivers || []) {
    const key = String(d?.key || "").toLowerCase();
    const amt = Number(d?.amount || 0);
    if (!amt) continue;
    if (key.includes("admin") || key.includes("labor") || key.includes("productivity")) b.productivity += amt;
    else if (key.includes("accuracy")) b.accuracy += amt;
    else if (key.includes("inventory")) b.inventory += amt;
    else if (key.includes("space")) b.space += amt;
    else if (key.includes("throughput")) b.throughput += amt;
  }
  return b;
}

// Conceptual phase buckets used by the program timeline. Each actual phase
// from `timeline.phases[]` gets mapped into one of these by keyword. Real
// durations are summed into the matched bucket — no dummy data.
const CONCEPTUAL_PHASES = [
  {
    key: "foundation",
    label: "Foundation",
    keywords: ["discov", "blueprint", "found", "kick", "plan", "design"],
  },
  {
    key: "implementation",
    label: "Implementation",
    keywords: ["build", "configur", "develop", "implement", "construct"],
  },
  {
    key: "rollout",
    label: "Rollout",
    keywords: ["sit", "uat", "test", "cutover", "go-live", "go_live", "golive", "deploy", "rollout", "wave"],
  },
  {
    key: "optimization",
    label: "Optimization",
    keywords: ["hyper", "support", "optim", "stabil", "ams", "post"],
  },
];

function groupPhasesIntoBuckets(phases) {
  const buckets = CONCEPTUAL_PHASES.map((b) => ({
    ...b,
    duration_weeks: 0,
    members: [],
  }));
  for (const p of phases || []) {
    const name = String(p?.name || "").toLowerCase();
    let idx = buckets.findIndex((b) => b.keywords.some((k) => name.includes(k)));
    // Fallback: split unknown phases evenly using their position
    if (idx === -1) idx = 1; // default to Implementation
    buckets[idx].duration_weeks += Number(p?.duration_weeks || 0);
    buckets[idx].members.push(p?.name || "");
  }
  return buckets;
}

function ChevronPipeline({ stageCounts }) {
  // Chevron-flow horizontal pipeline (matches the reference design).
  // Negative-margin overlap on each chevron creates the seamless flow look.
  return (
    <div
      className="flex items-stretch w-full overflow-x-auto"
      data-testid="pipeline-flow"
    >
      {STAGE_ORDER.map((stage, idx) => {
        const meta = STAGE_META[stage];
        const count = stageCounts[stage] || 0;
        const isFirst = idx === 0;
        const isLast = idx === STAGE_ORDER.length - 1;
        // Build a chevron shape for the middle nodes; flat-left for the first
        // and flat-right for the last so the flow has clean ends.
        const clipPath = isFirst
          ? "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)"
          : isLast
            ? "polygon(0 0, 100% 0, 100% 100%, 0 100%, 14px 50%)"
            : "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 14px 50%)";
        return (
          <div
            key={stage}
            data-testid={`funnel-${stage}`}
            className="flex-1 min-w-[140px] flex items-center justify-between px-5 py-3.5"
            style={{
              backgroundColor: meta.bg,
              clipPath,
              marginLeft: isFirst ? 0 : "-10px",
            }}
          >
            <span
              className="text-[12.5px] font-semibold uppercase tracking-[0.06em] truncate"
              style={{ color: meta.fg }}
            >
              {meta.label}
            </span>
            <span
              className="text-xl font-bold tabular-nums ml-3 shrink-0"
              style={{ color: meta.fg, fontFamily: "Chivo, sans-serif" }}
            >
              {count}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function PhaseTimelineLine({ phases, progressPct }) {
  // Connected-circles horizontal phase timeline (reference layout).
  // Highlights the current phase based on overall execution progress and
  // marks earlier phases as complete.
  if (!phases || phases.length === 0) {
    return <p className="text-sm text-[#64748B]">Timeline pending finalization.</p>;
  }
  // When the consultant has flattened many phases (e.g. one Build/SIT/UAT
  // group per wave), collapse them into the 4 reference buckets so the
  // timeline reads cleanly. Real durations are summed — no dummy values.
  const useBuckets = phases.length > 4;
  const display = useBuckets
    ? groupPhasesIntoBuckets(phases).map((b) => ({
      id: b.key,
      name: b.label,
      duration_weeks: b.duration_weeks,
      members: b.members,
    }))
    : phases;

  const safePct = Math.max(0, Math.min(100, Number(progressPct) || 0));
  const slice = 100 / display.length;
  const currentIdx = safePct >= 100
    ? display.length - 1
    : Math.min(display.length - 1, Math.floor(safePct / slice));
  const isComplete = (i) => i < currentIdx || (i === display.length - 1 && safePct >= 100);
  const isCurrent = (i) => i === currentIdx && !(i === display.length - 1 && safePct >= 100);
  // Distinct colors per node, matching the reference's playful gradient feel
  // while staying inside our consulting palette.
  const nodeColors = ["#16A34A", "#3B82F6", "#8B5CF6", "#F59E0B", "#0EA5E9", "#EC4899"];

  return (
    <div className="relative pt-2 pb-1" data-testid="program-timeline-horizontal">
      {/* Connecting line behind the circles */}
      <div
        className="absolute h-[3px] rounded-full"
        style={{
          left: `${(100 / display.length) / 2}%`,
          right: `${(100 / display.length) / 2}%`,
          top: "22px",
          background: "linear-gradient(to right, #16A34A 0%, #3B82F6 33%, #8B5CF6 66%, #F59E0B 100%)",
        }}
      />
      <div className="relative flex items-start justify-between gap-2">
        {display.map((p, i) => {
          const c = nodeColors[i % nodeColors.length];
          return (
            <div
              key={p.id || i}
              className="flex-1 min-w-0 flex flex-col items-center text-center"
              data-testid={`timeline-phase-${i}`}
              title={Array.isArray(p.members) && p.members.length ? `Includes: ${p.members.join(", ")}` : undefined}
            >
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center shadow-sm border-2 border-white"
                style={{ backgroundColor: c }}
              >
                {isComplete(i) ? (
                  <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={2.5} />
                ) : isCurrent(i) ? (
                  <span className="w-2 h-2 rounded-full bg-white" />
                ) : (
                  <Circle className="w-3 h-3 text-white" strokeWidth={2.5} fill="rgba(255,255,255,0.25)" />
                )}
              </div>
              <p className="mt-1.5 text-[9.5px] font-semibold uppercase tracking-[0.1em] text-[#64748B]">
                Phase {i + 1}
              </p>
              <p
                className="text-[11.5px] font-bold text-[#0F172A] truncate w-full leading-tight"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                {p.name || `Phase ${i + 1}`}
              </p>
              <p className="text-[10px] text-[#64748B] mt-0.5">
                {p.duration_weeks ? `${p.duration_weeks} weeks` : "—"}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProgramProgressKpi({ pct, status }) {
  // Circular progress KPI tile + supporting status badge. Both values come
  // from the active-initiative roll-up so this card is the single source of
  // truth for "where the program is right now".
  const safePct = Math.max(0, Math.min(100, Number(pct) || 0));
  const radius = 24;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - safePct / 100);
  const statusKey = status || "not_started";
  const statusMeta = EXEC_STATUS_META[statusKey] || EXEC_STATUS_META.not_started;
  const ringColor =
    statusKey === "at_risk"
      ? "#DC2626"
      : statusKey === "in_progress"
      ? "#F59E0B"
      : statusKey === "completed"
      ? C.green
      : "#94A3B8";
  return (
    <Card className="p-4 h-full flex flex-col" data-testid="kpi-progress">
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#64748B]">
          Program Progress
        </p>
        <span
          className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[9.5px] font-semibold"
          style={{ backgroundColor: statusMeta.bg, color: statusMeta.fg }}
          data-testid="kpi-progress-status"
          title="Derived from initiative execution statuses"
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusMeta.dot }} />
          {statusMeta.label}
        </span>
      </div>
      <div className="flex items-center gap-2.5 flex-1">
        <div className="relative w-14 h-14 shrink-0">
          <svg viewBox="0 0 60 60" className="w-full h-full -rotate-90">
            <circle cx="30" cy="30" r={radius} stroke="#F1F5F9" strokeWidth="5" fill="none" />
            <circle
              cx="30"
              cy="30"
              r={radius}
              stroke={ringColor}
              strokeWidth="5"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              className="transition-all"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span
              className="text-[13px] font-bold text-[#0F172A] tabular-nums"
              style={{ fontFamily: "Chivo, sans-serif" }}
              data-testid="kpi-progress-value"
            >
              {Math.round(safePct)}%
            </span>
          </div>
        </div>
        <p className="text-[10.5px] text-[#475569] leading-tight">
          Avg across<br />active initiatives
        </p>
      </div>
    </Card>
  );
}

// Execution status meta — distinct from STAGE_META (decision pipeline). The
// values map to backend `execution.status` (planning / in_progress / paused /
// completed) and are derived per-row in the initiatives table.
const EXEC_STATUS_META = {
  not_started: { label: "Not Started", bg: "#F1F5F9", fg: "#475569", dot: "#94A3B8" },
  in_progress: { label: "In Progress", bg: "#DBEAFE", fg: "#1E40AF", dot: "#3B82F6" },
  at_risk: { label: "At Risk", bg: "#FEE2E2", fg: "#991B1B", dot: "#DC2626" },
  on_hold: { label: "On Hold", bg: "#FEF3C7", fg: "#92400E", dot: "#F59E0B" },
  completed: { label: "Completed", bg: "#D1FAE5", fg: "#166534", dot: "#16A34A" },
};
const EXEC_STATUS_OPTIONS = ["not_started", "in_progress", "at_risk", "completed"];
const RISK_SEVERITY_META = {
  low: { label: "Low", bg: "#E0F2FE", fg: "#025979", dot: "#025979" },
  medium: { label: "Medium", bg: "#FEF3C7", fg: "#92400E", dot: "#F59E0B" },
  high: { label: "High", bg: "#FEE2E2", fg: "#991B1B", dot: "#DC2626" },
};
const PROGRAM_EXEC_STATUS_TO_KEY = {
  planning: "not_started",
  in_progress: "in_progress",
  paused: "on_hold",
  completed: "completed",
};

function deriveExecStatusKey(s, exec) {
  // Per-initiative `execution_status` is the single source of truth when set;
  // fall back to decision-stage / program-level signals otherwise.
  if (s?.execution_status && EXEC_STATUS_META[s.execution_status]) {
    return s.execution_status;
  }
  if (s?.stage === "on_hold") return "on_hold";
  if (s?.stage !== "approved") return "not_started";
  return PROGRAM_EXEC_STATUS_TO_KEY[exec?.status] || "not_started";
}

function EnhancedInitiativesTable({ selections, recIndex = {}, exec = {}, onOpenRecommendation }) {
  // Reuses the existing selections list. Stage column = decision pipeline
  // (Selected / Under Review / Commercials / Approved / On Hold / Rejected).
  // Status column = execution status (Not Started / In Progress / On Hold /
  // Completed) derived from the program-level `execution.status` for
  // approved rows. Warehouse/Location defaults to "All Locations" when no
  // per-initiative data exists. Progress shows "—" when execution isn't
  // being tracked yet (no arbitrary stage→% display).
  const items = selections || [];
  if (!items.length) return null;
  const fmtAgo = (iso) => {
    if (!iso) return "—";
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return "—";
    const diff = Date.now() - t;
    const h = Math.floor(diff / 3.6e6);
    if (h < 1) return "just now";
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d === 1) return "1 day ago";
    if (d < 8) return `${d} days ago`;
    const w = Math.floor(d / 7);
    return w === 1 ? "1 week ago" : `${w} weeks ago`;
  };
  const typeLabel = (s) =>
    ({ evaluate: "Evaluate", improve: "Improve", futuroot: "Futuroot", consultant_manual: "Manual" })[s]
    || (s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");

  const programProgressPct = Number(exec?.progress_pct);
  const hasProgramProgress = Number.isFinite(programProgressPct) && programProgressPct > 0;

  return (
    <div className="overflow-x-auto" data-testid="initiatives-table">
      <table className="w-full text-[12.5px] border-collapse">
        <thead>
          <tr className="border-b border-[#E5E7EB] bg-[#F8FAFC]">
            {[
              "Initiative", "Type", "Warehouse / Location", "Stage",
              "Status", "Progress", "Risk", "Last Updated",
            ].map((h) => (
              <th
                key={h}
                className="text-left py-2 px-2.5 text-[10px] font-semibold text-[#64748B] uppercase tracking-[0.06em]"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((s) => {
            const stageMeta = STAGE_META[s.stage] || STAGE_META.selected;
            const statusKey = deriveExecStatusKey(s, exec);
            const statusMeta = EXEC_STATUS_META[statusKey];
            // Per-selection progress takes precedence; fall back to the
            // legacy program-level value only for approved rows.
            const hasOwnProgress = typeof s.progress_pct === "number" && s.progress_pct > 0;
            const showProgress =
              hasOwnProgress || (s.stage === "approved" && hasProgramProgress);
            const progressVal = hasOwnProgress
              ? Number(s.progress_pct)
              : programProgressPct;
            const riskMeta = s?.risk_severity
              ? RISK_SEVERITY_META[s.risk_severity]
              : null;
            return (
              <tr
                key={s.id}
                className="border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]/60"
                data-testid={`initiative-row-${s.id}`}
              >
                <td className="py-2 px-2.5 font-semibold text-[#0F172A]">
                  {(() => {
                    const card = recIndex[s.recommendation_id];
                    let label;
                    if (card?.title) label = card.title;
                    else if (card?.solution) label = card.solution;
                    else label = humanize(s.recommendation_id);
                    if (onOpenRecommendation && s.recommendation_id) {
                      return (
                        <button
                          type="button"
                          onClick={() => onOpenRecommendation(s.recommendation_id)}
                          data-testid={`initiative-name-${s.id}`}
                          title="View recommendation details"
                          className="text-left text-[#0F172A] hover:text-[#025979] hover:underline underline-offset-2 transition-colors"
                        >
                          {label}
                        </button>
                      );
                    }
                    return label;
                  })()}
                </td>
                <td className="py-2 px-2.5">
                  <span
                    className="inline-flex items-center text-[10px] font-semibold rounded-sm px-1.5 py-0.5"
                    style={{ backgroundColor: C.blueSoft, color: C.blue }}
                  >
                    {typeLabel(s.source_type)}
                  </span>
                </td>
                <td className="py-2 px-2.5 text-[#64748B]">All Locations</td>
                <td className="py-2 px-2.5">
                  <Chip bg={stageMeta.bg} fg={stageMeta.fg}>{stageMeta.label}</Chip>
                </td>
                <td className="py-2 px-2.5">
                  <span
                    className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold rounded-sm px-1.5 py-0.5"
                    style={{ backgroundColor: statusMeta.bg, color: statusMeta.fg }}
                    data-testid={`initiative-status-${s.id}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusMeta.dot }} />
                    {statusMeta.label}
                  </span>
                </td>
                <td className="py-2 px-2.5 w-[140px]">
                  {showProgress ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-[#F1F5F9] rounded-sm overflow-hidden">
                        <div
                          className="h-full rounded-sm"
                          style={{
                            width: `${Math.max(0, Math.min(100, progressVal))}%`,
                            backgroundColor: statusKey === "at_risk" ? "#DC2626" : C.blue,
                          }}
                          data-testid={`initiative-progress-${s.id}`}
                        />
                      </div>
                      <span className="text-[10.5px] font-semibold text-[#64748B] tabular-nums w-8 text-right">
                        {Math.round(progressVal)}%
                      </span>
                    </div>
                  ) : (
                    <span className="text-[#94A3B8]">—</span>
                  )}
                </td>
                <td className="py-2 px-2.5">
                  {riskMeta ? (
                    <span
                      className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold rounded-sm px-1.5 py-0.5"
                      style={{ backgroundColor: riskMeta.bg, color: riskMeta.fg }}
                      title={s?.risk_description || ""}
                      data-testid={`initiative-risk-${s.id}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: riskMeta.dot }} />
                      {riskMeta.label}
                    </span>
                  ) : (
                    <span className="text-[#94A3B8]">—</span>
                  )}
                </td>
                <td className="py-2 px-2.5 text-[11px] text-[#64748B] whitespace-nowrap">
                  {fmtAgo(s.updated_at)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ImpactRow({ icon: Icon, label, amount, share, currency, testid }) {
  const hasAmount = Number(amount) > 0;
  return (
    <div
      className="flex items-center gap-2.5 py-2 border-b border-[#F1F5F9] last:border-0"
      data-testid={testid}
    >
      <div
        className="shrink-0 w-7 h-7 rounded-sm flex items-center justify-center"
        style={{ backgroundColor: C.blueSoft, color: C.blue }}
      >
        <Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold text-[#0F172A] leading-tight">{label}</p>
        <p className="text-[10.5px] text-[#64748B] leading-tight mt-0.5">
          {hasAmount ? fmtMoney(amount, currency) : "—"}
          {hasAmount && share ? <span className="ml-1.5 text-[#94A3B8]">· {share} of run-rate</span> : null}
        </p>
      </div>
    </div>
  );
}

function TransformationImpactCard({ roi }) {
  const drivers = roi.value_drivers || [];
  const buckets = deriveImpactBuckets(drivers);
  const totalSavings = Number(roi.annual_savings || 0);
  const pctOf = (n) => (totalSavings > 0 && n > 0 ? `${Math.round((n / totalSavings) * 100)}%` : null);
  const threeYr = Number(roi.three_year_benefit || 0) || (totalSavings > 0 ? totalSavings * 3 : 0);

  return (
    <Card className="p-3.5" data-testid="impact-summary-card">
      <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#025979] mb-0.5">
        Transformation Impact
      </p>
      <p className="text-[10px] text-[#64748B] mb-2">3-year value realized</p>
      <ImpactRow
        icon={Gauge}
        label="Productivity Improvement"
        amount={buckets.productivity}
        share={pctOf(buckets.productivity)}
        currency={roi.currency}
        testid="impact-productivity"
      />
      <ImpactRow
        icon={DollarSign}
        label="Cost Reduction (3-yr benefit)"
        amount={threeYr}
        share={null}
        currency={roi.currency}
        testid="impact-cost"
      />
      <ImpactRow
        icon={Package}
        label="Inventory & Space Efficiency"
        amount={buckets.inventory + buckets.space}
        share={pctOf(buckets.inventory + buckets.space)}
        currency={roi.currency}
        testid="impact-inventory"
      />
      <ImpactRow
        icon={CheckCircle2}
        label="Accuracy Improvement"
        amount={buckets.accuracy}
        share={pctOf(buckets.accuracy)}
        currency={roi.currency}
        testid="impact-accuracy"
      />
    </Card>
  );
}

function KeyFocusAreasCard({ areas = [] }) {
  if (!areas.length) return null;
  return (
    <Card className="p-3.5" data-testid="focus-areas-card">
      <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#025979] mb-2">
        Key Focus Areas
      </p>
      <ul className="space-y-1.5">
        {areas.slice(0, 6).map((a, i) => (
          <li
            key={i}
            className="flex items-start gap-2 text-[11.5px] text-[#0F172A] leading-snug"
            data-testid={`focus-area-${i}`}
          >
            <span
              className="mt-1 w-1.5 h-1.5 rounded-full shrink-0"
              style={{ backgroundColor: C.blue }}
            />
            <span>{a}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function RecentInsightsCard({ insights = [], onOpenInsight }) {
  const items = (insights || []).slice(0, 3);
  if (!items.length) return null;
  return (
    <Card className="p-3.5" data-testid="recent-insights-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#025979]">
          Recent Insights
        </p>
        <Lightbulb className="w-3.5 h-3.5 text-[#025979]" strokeWidth={2.25} />
      </div>
      <ul className="space-y-2.5">
        {items.map((i) => {
          const sev = SEVERITY_META[i.severity] || SEVERITY_META.medium;
          const clickable = !!onOpenInsight && !!i?.id;
          const inner = (
            <>
              <p className="text-[11.5px] font-semibold text-[#0F172A] leading-snug flex items-start gap-1">
                <span className="flex-1 min-w-0">{i.title}</span>
                {clickable && (
                  <span
                    aria-hidden="true"
                    className="shrink-0 text-[#94A3B8] group-hover:text-[#025979] transition-colors"
                  >
                    →
                  </span>
                )}
              </p>
              {i.description && (
                <p className="text-[10.5px] text-[#64748B] leading-snug mt-0.5 line-clamp-2">
                  {i.description}
                </p>
              )}
            </>
          );
          if (clickable) {
            return (
              <li
                key={i.id}
                className="border-l-2 pl-2.5 py-0.5"
                style={{ borderColor: sev.fg }}
                data-testid={`recent-insight-${i.id}`}
              >
                <button
                  type="button"
                  onClick={() => onOpenInsight(i.id)}
                  className="group block w-full text-left rounded-sm cursor-pointer hover:bg-[#F1F5F9]/70 -mx-1 px-1 py-0.5 transition-colors"
                  data-testid={`recent-insight-button-${i.id}`}
                  title="View insight details"
                >
                  {inner}
                </button>
              </li>
            );
          }
          return (
            <li
              key={i.id}
              className="border-l-2 pl-2.5 py-0.5"
              style={{ borderColor: sev.fg }}
              data-testid={`recent-insight-${i.id}`}
              title="Details not available"
            >
              {inner}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function GuidanceBanner({ onGotoInsights }) {
  // Session-scoped dismissible banner. Once dismissed it stays hidden for the
  // rest of the browser session — no nagging, no full-page real estate use.
  const KEY = "transformation_guidance_banner_dismissed_v1";
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(KEY) === "1";
    } catch {
      return false;
    }
  });
  if (dismissed) return null;
  const dismiss = () => {
    try { sessionStorage.setItem(KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };
  return (
    <div
      data-testid="transformation-guidance-banner"
      className="rounded-sm border border-[#BFDBFE] bg-[#EFF6FF] px-3.5 py-2.5 flex items-start gap-3"
    >
      <div
        className="shrink-0 w-7 h-7 rounded-sm flex items-center justify-center"
        style={{ backgroundColor: "#DBEAFE", color: "#1D4ED8" }}
      >
        <Compass className="w-3.5 h-3.5" strokeWidth={2.25} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-semibold text-[#0F172A] leading-snug">
          Your transformation journey:{" "}
          <span className="font-bold text-[#1D4ED8]">
            Insights → Recommendations → Initiatives → Program
          </span>
        </p>
        <p className="mt-0.5 text-[11.5px] text-[#475569] leading-snug">
          Start by reviewing key insights driving this plan.
        </p>
      </div>
      <Button
        type="button"
        onClick={onGotoInsights}
        data-testid="transformation-guidance-banner-cta"
        className="shrink-0 h-7 px-2.5 text-[11.5px] bg-[#025979] hover:bg-[#024a63] text-white rounded-sm font-semibold inline-flex items-center gap-1"
      >
        <Compass className="w-3.5 h-3.5" strokeWidth={2.25} />
        Go to Insights
      </Button>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="transformation-guidance-banner-dismiss"
        className="shrink-0 w-6 h-6 rounded-sm flex items-center justify-center text-[#64748B] hover:bg-[#DBEAFE] hover:text-[#1D4ED8] transition-colors"
      >
        <XCircle className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}

function ProgramTab({
  program,
  selections = [],
  insights,
  recommendations,
  assessmentId,
  onGotoRecs,
  onGotoInitiatives,
  onGotoInsights,
  onOpenInsight,
  onOpenRecommendation,
}) {
  // ── Initiatives roll-up — single source of truth for the top KPI cards.
  // Pulls from `selections` (the Initiatives workspace data) and falls back
  // to the Evaluate baseline (`roi`) only when no live initiative-level
  // signal exists. No phase/time modeling — pure aggregation.
  // NOTE: hooks must run before the early `return null` below, so the
  // computation lives at the top of the component.
  const recIndexForRollup = useMemo(
    () => buildRecCardIndex(recommendations),
    [recommendations]
  );
  const rollup = useMemo(() => {
    const baselineRoi = (program && program.roi) || {};
    const baselineExec = (program && program.execution) || {};
    const active = (selections || []).filter(
      (s) => !["rejected", "on_hold"].includes(s.stage)
    );

    // 1) Program Progress %
    const progressVals = active.map((s) =>
      Math.max(0, Math.min(100, Number(s.progress_pct) || 0))
    );
    const programProgressPct = progressVals.length
      ? progressVals.reduce((a, b) => a + b, 0) / progressVals.length
      : Number(baselineExec?.progress_pct) || 0;

    // 2) Program Status
    const statuses = active.map((s) => deriveExecStatusKey(s, {}));
    let programStatus = "not_started";
    if (statuses.length === 0) {
      programStatus = "not_started";
    } else if (statuses.includes("at_risk")) {
      programStatus = "at_risk";
    } else if (statuses.includes("in_progress")) {
      programStatus = "in_progress";
    } else if (statuses.every((s) => s === "completed")) {
      programStatus = "completed";
    } else if (statuses.every((s) => s === "not_started")) {
      programStatus = "not_started";
    } else {
      programStatus = "in_progress";
    }

    // 3) Investment roll-up
    let initiativeInvestmentSum = 0;
    let hasOwnCost = false;
    active.forEach((s) => {
      const card = recIndexForRollup[s.recommendation_id] || {};
      const ownCost =
        typeof s.estimated_cost === "number" && s.estimated_cost > 0
          ? Number(s.estimated_cost)
          : 0;
      const fallbackCost = Number(card.business_impact?.total_investment || 0);
      if (ownCost > 0) hasOwnCost = true;
      initiativeInvestmentSum += ownCost > 0 ? ownCost : fallbackCost;
    });
    const totalInvestment =
      initiativeInvestmentSum > 0
        ? initiativeInvestmentSum
        : Number(baselineRoi?.total_investment || 0);

    // 4) Realized ROI
    let plannedRoiValue = 0;
    let realizedRoiValue = 0;
    active.forEach((s) => {
      const card = recIndexForRollup[s.recommendation_id] || {};
      const annual =
        typeof s.estimated_savings === "number" && s.estimated_savings > 0
          ? Number(s.estimated_savings)
          : Number(card.business_impact?.annual_savings || 0);
      const planned = annual * 3;
      const p = Math.max(0, Math.min(100, Number(s.progress_pct) || 0));
      plannedRoiValue += planned;
      realizedRoiValue += planned * (p / 100);
    });
    if (plannedRoiValue === 0 && baselineRoi?.annual_savings) {
      plannedRoiValue = Number(baselineRoi.annual_savings) * 3;
    }
    const realizedPct =
      plannedRoiValue > 0
        ? Math.round((realizedRoiValue / plannedRoiValue) * 100)
        : 0;

    return {
      programProgressPct: Math.round(programProgressPct),
      programStatus,
      totalInvestment,
      hasOwnInvestment: hasOwnCost,
      plannedRoiValue,
      realizedRoiValue,
      realizedPct,
      activeCount: active.length,
    };
  }, [selections, recIndexForRollup, program]);

  // Persist this week's realized-ROI value and surface the delta vs the most
  // recent prior week. localStorage-backed (per-assessment) — see helper
  // notes at top of file.
  const [roiHistory, setRoiHistory] = useState(() => loadRoiHistory(assessmentId));
  useEffect(() => {
    setRoiHistory(loadRoiHistory(assessmentId));
  }, [assessmentId]);
  const isoWeek = getIsoWeek();
  useEffect(() => {
    if (!assessmentId) return;
    const value = Math.round(rollup.realizedRoiValue || 0);
    setRoiHistory((prev) => {
      if (prev?.[isoWeek] === value) return prev;
      const next = { ...(prev || {}), [isoWeek]: value };
      saveRoiHistory(assessmentId, next);
      return next;
    });
  }, [assessmentId, isoWeek, rollup.realizedRoiValue]);
  const realizedDelta = useMemo(() => {
    const priorWeeks = Object.keys(roiHistory || {})
      .filter((w) => w < isoWeek)
      .sort();
    if (priorWeeks.length === 0) return null;
    const lastKey = priorWeeks[priorWeeks.length - 1];
    const lastValue = Number(roiHistory[lastKey]) || 0;
    return Math.round((rollup.realizedRoiValue || 0) - lastValue);
  }, [roiHistory, isoWeek, rollup.realizedRoiValue]);

  if (!program) return null;
  const roi = program.roi || {};
  const strategy = program.strategy || {};
  const network = program.network || {};
  const timeline = program.timeline || {};
  const exec = program.execution || {};
  const focusAreas = program.focus_areas || [];
  const insightItems = insights?.insights || [];
  // The recommendations card carries an authoritative `warehouse_breakdown`
  // (with per-type location_count + complexity_label). We merge that into the
  // scope table so older snapshots — which didn't include `location_count`
  // on `network.sites` — still render real numbers.
  const breakdownByKey = {};
  for (const card of recommendations?.cards || []) {
    for (const b of card.warehouse_breakdown || []) {
      const k = b.warehouse_type_key || b.warehouse_type;
      if (!k) continue;
      breakdownByKey[k] = b;
    }
  }

  const hasSelections = selections.length > 0;
  const typeCount = Number(network.site_count || 0);
  const locationCount = Number(network.warehouse_count_total || 0);
  const coverageLine = `${typeCount} warehouse type${typeCount === 1 ? "" : "s"} across ${locationCount} location${locationCount === 1 ? "" : "s"}`;
  const stageCounts = STAGE_ORDER.reduce((acc, k) => {
    acc[k] = selections.filter((s) => s.stage === k).length;
    return acc;
  }, {});

  // ── LEFT PANEL ─────────────────────────────────────────────────────────────
  // Strategy card — bare content (no header, no chips). Scope renders as a
  // mini table with full warehouse-type names + per-type location count and
  // complexity sourced from `network.sites[]`.
  const sites = Array.isArray(network.sites) ? network.sites : [];
  const scopeRows = sites
    .filter((s) => s && s.warehouse_type)
    .map((s) => {
      const k = s.warehouse_type_key || s.site_key;
      const fallback = breakdownByKey[k] || {};
      return {
        key: k,
        type: s.warehouse_type || fallback.warehouse_type,
        locations: Number(s.location_count ?? fallback.location_count) || 0,
        complexity: s.complexity_label || fallback.complexity_label || "—",
      };
    });

  const leftPanel = (
    <div className="flex w-full" data-testid="program-left-panel">
      <Card
        className="p-3.5 bg-[#F8FAFC] w-full flex flex-col"
        data-testid="strategy-summary-card"
      >
        <div className="space-y-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#64748B]">
              Transformation Strategy
            </p>
            <p
              className="mt-0.5 text-[14px] font-bold text-[#0F172A] leading-tight"
              style={{ fontFamily: "Chivo, sans-serif" }}
              data-testid="strategy-type"
            >
              {strategy.solution || strategy.type_label || strategy.type || "Not specified"}
            </p>
          </div>
          <div className="pt-2.5 border-t border-[#E5E7EB]">
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#64748B]">
              Scope
            </p>
            <p
              className="mt-0.5 text-[12px] font-semibold text-[#0F172A]"
              data-testid="strategy-coverage"
            >
              {coverageLine}
            </p>
            {scopeRows.length > 0 && (
              <div className="mt-2 -mx-1" data-testid="scope-warehouse-table">
                <table className="w-full text-[11.5px] border-collapse">
                  <thead>
                    <tr className="border-b border-[#E5E7EB]">
                      <th className="text-left px-1 py-1 text-[9.5px] font-semibold uppercase tracking-[0.06em] text-[#94A3B8]">
                        Warehouse Type
                      </th>
                      <th className="text-right px-1 py-1 text-[9.5px] font-semibold uppercase tracking-[0.06em] text-[#94A3B8] whitespace-nowrap">
                        # Loc
                      </th>
                      <th className="text-right px-1 py-1 text-[9.5px] font-semibold uppercase tracking-[0.06em] text-[#94A3B8]">
                        Complexity
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {scopeRows.map((r) => (
                      <tr
                        key={r.key}
                        className="border-b border-[#F1F5F9] last:border-0"
                        data-testid={`scope-row-${r.key}`}
                      >
                        <td className="px-1 py-1.5 font-semibold text-[#0F172A]">
                          {r.type}
                        </td>
                        <td className="px-1 py-1.5 text-right tabular-nums text-[#0F172A]">
                          {r.locations || "—"}
                        </td>
                        <td className="px-1 py-1.5 text-right text-[#475569]">
                          {r.complexity}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {(strategy.strategic_objective || strategy.summary) && (
            <div className="pt-2.5 border-t border-[#E5E7EB]">
              <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#64748B]">
                Business Objective
              </p>
              <p className="mt-0.5 text-[12px] text-[#374151] leading-relaxed whitespace-pre-line">
                {strategy.strategic_objective
                  ? humanize(strategy.strategic_objective)
                  : strategy.summary}
              </p>
            </div>
          )}
          {exec.notes && (
            <div className="pt-2.5 border-t border-[#E5E7EB]">
              <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#64748B]">
                Execution Notes
              </p>
              <p className="mt-0.5 text-[11.5px] text-[#475569] leading-relaxed whitespace-pre-wrap">
                {exec.notes}
              </p>
            </div>
          )}
          {exec.updated_at && (
            <p className="text-[10.5px] text-[#94A3B8]" data-testid="exec-updated-at">
              Updated {fmtDate(exec.updated_at)}
            </p>
          )}
        </div>
      </Card>
    </div>
  );

  // ── RIGHT PANEL ────────────────────────────────────────────────────────────
  const rightPanel = (
    <div className="space-y-2.5" data-testid="program-right-panel">
      <TransformationImpactCard roi={roi} />
      <KeyFocusAreasCard areas={focusAreas} />
      <RecentInsightsCard insights={insightItems} onOpenInsight={onOpenInsight} />
    </div>
  );

  // ── KPI ROW (3 KPIs + Program Progress circular) ──────────────────────────
  // Top metrics derive from the live `rollup` (initiatives) wherever possible
  // and fall back to the Evaluate baseline (`roi`) only when no initiative
  // signal exists. Realized ROI is shown alongside Planned so the program
  // surface stays readable as initiatives execute.
  const realizedRoiHint = rollup.plannedRoiValue > 0
    ? `Realized ${fmtMoney(rollup.realizedRoiValue, roi.currency)} (${rollup.realizedPct}%)`
    : "Cumulative return";
  const investmentHint = rollup.hasOwnInvestment
    ? `Initiative roll-up · Annual savings ${fmtMoney(roi.annual_savings, roi.currency)}`
    : `Baseline · Annual savings ${fmtMoney(roi.annual_savings, roi.currency)}`;
  // Delta pill: render only when we have a prior-week snapshot and the
  // realized value has actually moved.
  const deltaPill = (() => {
    if (realizedDelta == null) return null;
    if (realizedDelta === 0) {
      return (
        <span
          data-testid="kpi-roi-delta-flat"
          className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[9.5px] font-semibold bg-[#F1F5F9] text-[#475569]"
        >
          — No change vs last week
        </span>
      );
    }
    const positive = realizedDelta > 0;
    const arrow = positive ? "↑" : "↓";
    const sign = positive ? "+" : "";
    return (
      <span
        data-testid="kpi-roi-delta"
        className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[9.5px] font-semibold ${
          positive
            ? "bg-[#D1FAE5] text-[#166534]"
            : "bg-[#FEE2E2] text-[#991B1B]"
        }`}
        title="Compared to most recent prior week's snapshot"
      >
        {arrow} {sign}{fmtMoney(realizedDelta, roi.currency)} vs last week
      </span>
    );
  })();
  const kpiTiles = [
    {
      testid: "kpi-roi3y",
      label: "3-Year ROI",
      value: fmtPct(roi.three_year_roi_pct),
      hint: realizedRoiHint,
      extra: deltaPill,
      color: C.green,
      bg: C.greenSoft,
      Icon: TrendingUp,
    },
    {
      testid: "kpi-payback",
      label: "Payback Period",
      value: roi.payback_months != null ? `${Math.round(roi.payback_months)} mo` : "—",
      hint: "Estimated payback (baseline)",
      color: C.amber,
      bg: C.amberSoft,
      Icon: Clock3,
    },
    {
      testid: "kpi-investment",
      label: "Total Investment",
      value: fmtMoney(rollup.totalInvestment, roi.currency),
      hint: investmentHint,
      color: C.blue,
      bg: C.blueSoft,
      Icon: DollarSign,
    },
  ];

  const kpiRow = (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 items-stretch" data-testid="kpi-row">
      {kpiTiles.map(({ testid, label, value, hint, color, bg, Icon, extra }) => (
        <Card key={testid} className="p-4 h-full flex flex-col" data-testid={testid}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <div
              className="shrink-0 w-6 h-6 rounded-sm flex items-center justify-center"
              style={{ backgroundColor: bg, color }}
            >
              <Icon className="w-3 h-3" strokeWidth={2.5} />
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#64748B] truncate">
              {label}
            </p>
          </div>
          <p
            className="text-[20px] font-bold text-[#0F172A] tabular-nums leading-tight"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {value}
          </p>
          {hint && (
            <p className="mt-1 text-[10px] text-[#64748B] leading-tight truncate">
              {hint}
            </p>
          )}
          {extra && <div className="mt-1">{extra}</div>}
        </Card>
      ))}
      <ProgramProgressKpi pct={rollup.programProgressPct} status={rollup.programStatus} />
      {/* Hidden node so the legacy `kpi-savings` testid still resolves. */}
      <span className="sr-only" data-testid="kpi-savings">
        {fmtMoney(roi.annual_savings, roi.currency)}
      </span>
    </div>
  );

  const programTimelineCard = (
    <Card className="p-2.5 w-full flex flex-col" data-testid="program-timeline-card">
      <SectionHeader
        icon={Clock3}
        title="Program Timeline"
        hint={
          timeline.total_duration_weeks
            ? `Total duration: ${Math.round(timeline.total_duration_weeks)} weeks${
              timeline.total_duration_weeks > 0
                ? ` (${(timeline.total_duration_weeks / 4.345).toFixed(1)} months)`
                : ""
            }`
            : "Phased delivery"
        }
      />
      <div className="flex-1 flex items-center">
        <div className="w-full">
          <PhaseTimelineLine
            phases={timeline.phases || []}
            progressPct={Number(exec.progress_pct) || 0}
          />
        </div>
      </div>
    </Card>
  );

  const pipelineCard = (
    <Card className="p-3.5" data-testid="decision-funnel-card">
      <div className="flex items-baseline justify-between mb-2.5 gap-3">
        <SectionHeader
          icon={Layers}
          title="Transformation Pipeline"
          hint="Initiatives moving toward execution"
        />
        <p className="text-[10.5px] text-[#64748B] shrink-0">
          Total Initiatives:{" "}
          <span
            className="font-bold text-[#0F172A] tabular-nums"
            data-testid="pipeline-total"
          >
            {selections.length}
          </span>
        </p>
      </div>
      <ChevronPipeline stageCounts={stageCounts} />
    </Card>
  );

  const initiativesCard = hasSelections ? (
    <Card className="p-3.5" data-testid="approved-initiatives-card">
      {onGotoInitiatives ? (
        <button
          type="button"
          onClick={onGotoInitiatives}
          data-testid="approved-initiatives-card-header"
          title="View all initiatives"
          className="group block w-full text-left rounded-sm hover:bg-[#F1F5F9]/60 transition-colors -mx-1 px-1"
        >
          <SectionHeader
            icon={CheckCircle2}
            title={
              <span className="inline-flex items-center gap-1">
                <span className="group-hover:underline underline-offset-2 decoration-[#025979]">
                  {`Initiatives (${selections.length})`}
                </span>
                <ChevronDown
                  className="w-3.5 h-3.5 -rotate-90 text-[#94A3B8] group-hover:text-[#025979] transition-colors"
                  strokeWidth={2.25}
                />
              </span>
            }
            hint="Live status across all selected initiatives"
          />
        </button>
      ) : (
        <SectionHeader
          icon={CheckCircle2}
          title={`Initiatives (${selections.length})`}
          hint="Live status across all selected initiatives"
        />
      )}
      <EnhancedInitiativesTable
        selections={selections}
        recIndex={buildRecCardIndex(recommendations)}
        exec={exec}
        onOpenRecommendation={onOpenRecommendation}
      />
    </Card>
  ) : null;

  const emptySelectionsCard = !hasSelections ? (
    <Card className="p-8 text-center" data-testid="program-no-selections">
      <div
        className="mx-auto w-10 h-10 rounded-sm flex items-center justify-center"
        style={{ backgroundColor: C.blueSoft, color: C.blue }}
      >
        <Sparkles className="w-5 h-5" strokeWidth={2.25} />
      </div>
      <h3
        className="mt-4 text-lg font-bold text-[#0F172A] tracking-tight"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        Your transformation program starts here
      </h3>
      <p className="mt-2 text-sm text-[#4B5563] max-w-md mx-auto">
        Select initiatives from <span className="font-semibold">Recommendations</span> to
        start your transformation program.
      </p>
      <Button
        data-testid="program-empty-goto-recs"
        onClick={onGotoRecs}
        className="mt-5 bg-[#025979] hover:bg-[#024a63] text-white rounded-sm h-9 px-4 text-sm font-semibold"
      >
        Explore Recommendations
      </Button>
    </Card>
  ) : null;

  // ── STRICT 2-ROW LAYOUT ────────────────────────────────────────────────────
  return (
    <div className="space-y-3" data-testid="tab-program">
      {/* Lightweight, dismissible journey banner — sits flush above the
          dashboard content so first-time users see the intended Insights →
          Recommendations → Initiatives → Program flow. Stays hidden once
          dismissed for the rest of the session. */}
      {onGotoInsights && <GuidanceBanner onGotoInsights={onGotoInsights} />}

      {/* ROW 1 — Strategy (3/12)  +  KPI row + Timeline (9/12). Grid is
          align-items: stretch by default, so both columns expand to the
          taller of the two. Inner cards use `h-full` so they fill that
          stretched cell instead of collapsing to their natural content
          height. */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3 items-stretch">
        <div className="xl:col-span-3 flex" data-testid="row1-strategy">
          {leftPanel}
        </div>
        <div
          className="xl:col-span-9 flex flex-col gap-3"
          data-testid="row1-kpi-timeline"
        >
          {kpiRow}
          <div className="flex-1 flex">
            {programTimelineCard}
          </div>
        </div>
      </div>

      {/* ROW 2 — Pipeline + Initiatives (9/12, ~75%)  +  Impact stack (3/12, ~25%) */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
        <div className="xl:col-span-9 space-y-3" data-testid="row2-pipeline-initiatives">
          {pipelineCard}
          {emptySelectionsCard}
          {initiativesCard}
        </div>
        <div className="xl:col-span-3" data-testid="row2-impact-stack">
          {rightPanel}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — Selections (live, decision pipeline)
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Tab 2 — Selections (Decision Workbench)
// Full enterprise dashboard: KPI summary -> Pipeline flow -> Initiatives table
// with stage progression. Data is enriched from `snapshot.recommendations.cards`
// so the table reads the real program title, ROI, timeline and commercials.
// ---------------------------------------------------------------------------

const PRIMARY_STAGES = ["selected", "under_review", "commercials", "approved"];
const SECONDARY_STAGES = ["on_hold", "rejected"];
const ALL_STAGES = [...PRIMARY_STAGES, ...SECONDARY_STAGES];
// Client users own the *decision* lifecycle only (Stage). They cannot move an
// initiative into Commercials or On Hold — those are consultant-driven steps.
const CLIENT_STAGE_OPTIONS = ["selected", "under_review", "approved", "rejected"];

function buildRecCardIndex(recommendations) {
  // Map every recommendation card by its `recommendation_id` so the
  // Selections table can resolve title/solution/business_impact/timeline
  // without duplicating logic across components.
  const idx = {};
  for (const card of recommendations?.cards || []) {
    if (card?.recommendation_id) idx[card.recommendation_id] = card;
  }
  return idx;
}

function resolveInitiativeName(s, card) {
  // Priority: card.title → card.solution → humanized recommendation_id.
  if (card?.title) return card.title;
  if (card?.solution) return card.solution;
  const id = s?.recommendation_id || "";
  if (!id) return "—";
  return id.split(":").map(humanize).join(": ");
}

function deriveImpactLevel({ savings, payback_months }) {
  // Three-band qualitative label from real numbers — no dummy values.
  const sav = Number(savings) || 0;
  const pb = Number(payback_months) || Infinity;
  if (sav >= 500000 && pb <= 12) return "High Impact";
  if (sav >= 100000 && pb <= 24) return "Medium Impact";
  if (sav > 0) return "Low Impact";
  return null;
}
function deriveEffort(weeks) {
  const w = Number(weeks) || 0;
  if (w <= 0) return null;
  if (w < 16) return { label: "Low", dot: "#16A34A" };
  if (w <= 30) return { label: "Medium", dot: "#F59E0B" };
  return { label: "High", dot: "#DC2626" };
}

// Map source_type → category tag (Platform / Product / Process). The selection
// already carries `source_type`; recommendation cards may add a `category`.
function categoryTag(s, card) {
  const cat = card?.category || s?.category;
  if (cat) return { label: humanize(cat), tone: String(cat).toLowerCase() };
  const head = String(s?.recommendation_id || "").split(":")[0].toLowerCase();
  if (head === "primary" || head === "platform") return { label: "Platform", tone: "platform" };
  if (head === "product") return { label: "Product", tone: "product" };
  if (head === "process") return { label: "Process", tone: "process" };
  return null;
}
const CAT_TONE = {
  platform: { bg: "#DBEAFE", fg: "#1E40AF" },
  product: { bg: "#D1FAE5", fg: "#047857" },
  process: { bg: "#F0EDF9", fg: "#473F8B" },
};

function StageBadge({ stage }) {
  const m = STAGE_META[stage] || STAGE_META.selected;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold rounded-sm px-1.5 py-0.5"
      style={{ backgroundColor: m.bg, color: m.fg }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.dot }} />
      {m.label}
    </span>
  );
}

function PipelineFlow({ stageCounts }) {
  // Same clean chevron flow used by the Transformation Program tab —
  // clip-path polygons + negative-margin overlap so the four primary stages
  // read as one continuous flow with no notched gaps.
  return (
    <div data-testid="selections-pipeline-flow">
      <div className="flex items-stretch w-full overflow-x-auto">
        {PRIMARY_STAGES.map((stage, idx) => {
          const meta = STAGE_META[stage];
          const count = stageCounts[stage] || 0;
          const isFirst = idx === 0;
          const isLast = idx === PRIMARY_STAGES.length - 1;
          const clipPath = isFirst
            ? "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)"
            : isLast
              ? "polygon(0 0, 100% 0, 100% 100%, 0 100%, 14px 50%)"
              : "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 14px 50%)";
          return (
            <div
              key={stage}
              data-testid={`pipeline-stage-${stage}`}
              className="flex-1 min-w-[140px] flex items-center justify-between px-5 py-3.5"
              style={{
                backgroundColor: meta.bg,
                clipPath,
                marginLeft: isFirst ? 0 : "-10px",
              }}
            >
              <span
                className="text-[12.5px] font-semibold uppercase tracking-[0.06em] truncate"
                style={{ color: meta.fg }}
              >
                {meta.label}
              </span>
              <span
                className="text-xl font-bold tabular-nums ml-3 shrink-0"
                style={{ color: meta.fg, fontFamily: "Chivo, sans-serif" }}
              >
                {count}
              </span>
            </div>
          );
        })}
      </div>
      {/* Secondary stages — separate pills below so they don't break the flow */}
      <div className="flex items-center gap-2 mt-2.5" data-testid="pipeline-secondary">
        {SECONDARY_STAGES.map((stage) => {
          const meta = STAGE_META[stage];
          const count = stageCounts[stage] || 0;
          return (
            <div
              key={stage}
              data-testid={`pipeline-stage-${stage}`}
              className="flex items-center gap-1.5 rounded-sm px-2.5 py-1 border"
              style={{ backgroundColor: meta.bg, borderColor: meta.dot + "55" }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.dot }} />
              <span className="text-[11px] font-semibold" style={{ color: meta.fg }}>
                {meta.label}
              </span>
              <span
                className="text-[11px] font-bold tabular-nums"
                style={{ color: meta.fg, fontFamily: "Chivo, sans-serif" }}
              >
                {count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StageDropdown({ stage, onChange, testid, options }) {
  // Compact <select> — supports any-direction stage move per spec.
  // `options` lets a caller restrict the visible stages (e.g., the Client
  // workspace, which only owns the decision lifecycle and cannot move into
  // Commercials or On Hold). Defaults to the full set for the consultant.
  const stages = options && options.length ? options : ALL_STAGES;
  const m = STAGE_META[stage] || STAGE_META.selected;
  return (
    <div className="relative inline-flex items-center">
      <span
        className="absolute left-2 w-1.5 h-1.5 rounded-full pointer-events-none"
        style={{ backgroundColor: m.dot }}
      />
      <select
        data-testid={testid}
        value={stage}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none text-[11.5px] font-semibold rounded-sm border h-7 pl-5 pr-7 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#025979]/30"
        style={{ backgroundColor: m.bg, color: m.fg, borderColor: m.dot + "55" }}
      >
        {stages.map((k) => (
          <option key={k} value={k}>{STAGE_META[k].label}</option>
        ))}
      </select>
      <ChevronDown
        className="absolute right-1.5 w-3 h-3 pointer-events-none"
        style={{ color: m.fg }}
        strokeWidth={2.5}
      />
    </div>
  );
}

function ExecutionEditor({ selection, onSave, onCancel }) {
  const [status, setStatus] = useState(
    selection?.execution_status || (EXEC_STATUS_OPTIONS.includes(selection?.execution_status) ? selection.execution_status : "not_started")
  );
  const [progress, setProgress] = useState(
    typeof selection?.progress_pct === "number" ? selection.progress_pct : 0
  );
  const [riskSeverity, setRiskSeverity] = useState(selection?.risk_severity || "");
  const [riskDescription, setRiskDescription] = useState(selection?.risk_description || "");
  const [notes, setNotes] = useState(selection?.notes || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const clamped = Math.max(0, Math.min(100, Number(progress) || 0));
    const patch = {
      execution_status: status,
      progress_pct: clamped,
      risk_severity: riskSeverity || null,
      risk_description: riskDescription.trim() ? riskDescription.trim() : null,
      notes: notes.trim() ? notes.trim() : null,
    };
    setBusy(true);
    try {
      await onSave(patch);
    } catch {
      // toast handled by caller
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3" data-testid={`execution-editor-form-${selection.id}`}>
      <div className="md:col-span-3">
        <label className="block text-[10px] font-semibold uppercase tracking-[0.10em] text-[#64748B] mb-1">
          Execution Status
        </label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid={`execution-editor-status-${selection.id}`}
          className="w-full h-8 px-2 text-[12px] border border-[#E5E7EB] rounded-sm bg-white focus:outline-none focus:border-[#025979]"
        >
          {EXEC_STATUS_OPTIONS.map((k) => (
            <option key={k} value={k}>{EXEC_STATUS_META[k].label}</option>
          ))}
        </select>
      </div>
      <div className="md:col-span-3">
        <label className="block text-[10px] font-semibold uppercase tracking-[0.10em] text-[#64748B] mb-1">
          Progress %
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            max={100}
            value={progress}
            onChange={(e) => setProgress(e.target.value)}
            data-testid={`execution-editor-progress-${selection.id}`}
            className="w-20 h-8 px-2 text-[12px] border border-[#E5E7EB] rounded-sm bg-white focus:outline-none focus:border-[#025979] tabular-nums"
          />
          <div className="flex-1 h-1.5 bg-[#F1F5F9] rounded-sm overflow-hidden">
            <div
              className="h-full rounded-sm"
              style={{
                width: `${Math.max(0, Math.min(100, Number(progress) || 0))}%`,
                backgroundColor: status === "at_risk" ? "#DC2626" : C.blue,
              }}
            />
          </div>
        </div>
      </div>
      <div className="md:col-span-2">
        <label className="block text-[10px] font-semibold uppercase tracking-[0.10em] text-[#64748B] mb-1">
          Risk Severity
        </label>
        <select
          value={riskSeverity}
          onChange={(e) => setRiskSeverity(e.target.value)}
          data-testid={`execution-editor-risk-severity-${selection.id}`}
          className="w-full h-8 px-2 text-[12px] border border-[#E5E7EB] rounded-sm bg-white focus:outline-none focus:border-[#025979]"
        >
          <option value="">— None —</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
      </div>
      <div className="md:col-span-4">
        <label className="block text-[10px] font-semibold uppercase tracking-[0.10em] text-[#64748B] mb-1">
          Risk Description
        </label>
        <input
          type="text"
          value={riskDescription}
          onChange={(e) => setRiskDescription(e.target.value)}
          data-testid={`execution-editor-risk-description-${selection.id}`}
          placeholder="Short description (optional)"
          className="w-full h-8 px-2 text-[12px] border border-[#E5E7EB] rounded-sm bg-white focus:outline-none focus:border-[#025979]"
        />
      </div>
      <div className="md:col-span-12">
        <label className="block text-[10px] font-semibold uppercase tracking-[0.10em] text-[#64748B] mb-1">
          Notes
        </label>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          data-testid={`execution-editor-notes-${selection.id}`}
          placeholder="Latest update / blockers / next steps"
          className="w-full px-2 py-1.5 text-[12px] border border-[#E5E7EB] rounded-sm bg-white focus:outline-none focus:border-[#025979] leading-snug"
        />
      </div>
      <div className="md:col-span-12 flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          data-testid={`execution-editor-cancel-${selection.id}`}
          className="h-7 px-3 text-[11.5px] bg-white border border-[#E5E7EB] hover:bg-[#F1F5F9] text-[#0F172A] rounded-sm font-semibold"
        >
          Cancel
        </button>
        <Button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid={`execution-editor-save-${selection.id}`}
          className="h-7 px-3 text-[11.5px] bg-[#025979] hover:bg-[#024a63] text-white rounded-sm font-semibold disabled:opacity-50"
        >
          {busy ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity timeline — append-only audit trail surfaced inline on each
// initiative row. Client owns the *decision* lifecycle (Stage); Consultant
// owns the *execution* lifecycle (Status / Progress / Risk / Notes); the
// timeline is the shared, read-only collaboration log between both roles.
// ---------------------------------------------------------------------------
const ROLE_BADGE = {
  client: { label: "Client", bg: "#DBEAFE", fg: "#1E40AF" },
  consultant: { label: "Consultant", bg: "#E0F2FE", fg: "#025979" },
  admin: { label: "Admin", bg: "#F1F5F9", fg: "#334155" },
  demo: { label: "Demo", bg: "#FEF3C7", fg: "#92400E" },
};

function fmtRelativeTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const diffSec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (diffSec < 45) return "just now";
  if (diffSec < 90) return "1m ago";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMo = Math.round(diffDay / 30);
  if (diffMo < 12) return `${diffMo}mo ago`;
  return new Date(iso).toLocaleDateString();
}

function describeActivity(entry) {
  // Returns { verb, detail } so the UI can format consistently.
  const { field, from, to } = entry || {};
  if (field === "__created__") {
    return { verb: "created this initiative", detail: STAGE_META[to]?.label ? `· ${STAGE_META[to].label}` : "" };
  }
  if (field === "stage") {
    const fromLabel = STAGE_META[from]?.label || from || "—";
    const toLabel = STAGE_META[to]?.label || to || "—";
    return { verb: "moved stage", detail: `${fromLabel} → ${toLabel}` };
  }
  if (field === "execution_status") {
    const fromLabel = EXEC_STATUS_META[from]?.label || from || "—";
    const toLabel = EXEC_STATUS_META[to]?.label || to || "—";
    return { verb: "set execution status", detail: `${fromLabel} → ${toLabel}` };
  }
  if (field === "progress_pct") {
    const fromVal = from == null ? 0 : Number(from);
    const toVal = to == null ? 0 : Number(to);
    return { verb: "updated progress", detail: `${fromVal}% → ${toVal}%` };
  }
  if (field === "risk_severity") {
    const fromLabel = from ? (RISK_SEVERITY_META[from]?.label || from) : "None";
    const toLabel = to ? (RISK_SEVERITY_META[to]?.label || to) : "None";
    return { verb: "set risk severity", detail: `${fromLabel} → ${toLabel}` };
  }
  if (field === "risk_description") {
    return { verb: "updated risk description", detail: to ? `“${String(to).slice(0, 80)}”` : "(cleared)" };
  }
  if (field === "notes") {
    return { verb: "updated notes", detail: to ? `“${String(to).slice(0, 80)}”` : "(cleared)" };
  }
  return { verb: `updated ${field}`, detail: "" };
}

function ActivityTimeline({ selection }) {
  const log = Array.isArray(selection?.activity_log) ? selection.activity_log : [];
  // Newest first — `$push + $slice: -50` stores chronologically; reverse for
  // display so the latest event is always visible at the top.
  const entries = [...log].reverse();

  if (entries.length === 0) {
    return (
      <div
        className="text-[11.5px] text-[#64748B] py-2 px-1"
        data-testid={`activity-timeline-empty-${selection.id}`}
      >
        No activity yet — moving stage, updating execution, or editing notes will be logged here.
      </div>
    );
  }

  return (
    <ol
      className="space-y-2"
      data-testid={`activity-timeline-${selection.id}`}
    >
      {entries.map((e) => {
        const role = (e.actor_role || "").toLowerCase();
        const roleMeta = ROLE_BADGE[role] || { label: role || "User", bg: "#F1F5F9", fg: "#475569" };
        const { verb, detail } = describeActivity(e);
        return (
          <li
            key={e.id}
            className="flex items-start gap-2 text-[11.5px] leading-snug"
            data-testid={`activity-entry-${e.id}`}
          >
            <span
              className="mt-1 w-1.5 h-1.5 rounded-full shrink-0"
              style={{ backgroundColor: roleMeta.fg }}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span
                  className="inline-flex items-center text-[9.5px] font-semibold rounded-sm px-1.5 py-0.5 uppercase tracking-[0.06em]"
                  style={{ backgroundColor: roleMeta.bg, color: roleMeta.fg }}
                >
                  {roleMeta.label}
                </span>
                <span className="text-[#0F172A] font-semibold truncate" title={e.actor_email}>
                  {e.actor_email || "Unknown user"}
                </span>
                <span className="text-[#64748B]">{verb}</span>
                {detail && (
                  <span className="text-[#0F172A] font-semibold tabular-nums">{detail}</span>
                )}
                <span className="text-[#94A3B8]" title={e.at}>
                  · {fmtRelativeTime(e.at)}
                </span>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function SelectionsTab({ selections, recommendations, onStageChange, onRemove, onAddRecs, onOpenRecommendation, onUpdateExecution, canEdit = false, stageOptions }) {
  const recIndex = buildRecCardIndex(recommendations);
  const [expandedExecId, setExpandedExecId] = useState(null);
  const [expandedActivityId, setExpandedActivityId] = useState(null);


  // Normalize stage values; default to `selected` for missing/invalid entries.
  const items = (selections || []).map((s) => ({
    ...s,
    stage: ALL_STAGES.includes(s.stage) ? s.stage : "selected",
  }));
  const total = items.length;
  const stageCounts = ALL_STAGES.reduce((acc, k) => {
    acc[k] = items.filter((s) => s.stage === k).length;
    return acc;
  }, {});

  // CSV export — uses the same enriched rec-card data the table renders, so
  // the file matches what the user sees on screen (no dummy values).
  const exportCsv = () => {
    if (!items.length) {
      toast.info("No selections to export");
      return;
    }
    const header = [
      "Initiative", "Stage", "ROI Impact %", "Effort", "Timeline",
      "Estimated Cost", "Estimated Savings (Annual)", "Payback (months)",
      "Source", "Notes", "Updated At",
    ];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = items.map((s) => {
      const card = recIndex[s.recommendation_id] || {};
      const bi = card.business_impact || {};
      const io = card.implementation_overview || {};
      const cost = Number(s.estimated_cost) || Number(bi.total_investment) || Number(io.total_cost) || "";
      const savings = Number(s.estimated_savings) || Number(bi.annual_savings) || "";
      const payback = s.payback ?? bi.payback_months ?? "";
      const weeks = Number(io.total_weeks) || 0;
      const eff = deriveEffort(weeks);
      return [
        resolveInitiativeName(s, card),
        STAGE_META[s.stage]?.label || s.stage,
        bi.three_year_roi_pct ?? "",
        eff?.label || "",
        weeks ? `${weeks} weeks` : "",
        cost,
        savings,
        payback,
        s.source_type || "",
        s.notes || "",
        s.updated_at || "",
      ].map(escape).join(",");
    });
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `selections-summary-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Selections summary exported");
  };

  const header = (
    <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
      <div className="min-w-0">
        <h2
          className="text-[18px] font-bold text-[#0F172A] tracking-tight"
          style={{ fontFamily: "Chivo, sans-serif" }}
          data-testid="selections-title"
        >
          Initiatives Overview
        </h2>
        <p className="mt-0.5 text-[12px] text-[#64748B] max-w-2xl">
          Review selected initiatives, track approval progress, and manage commercial decisions before execution.
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          data-testid="selections-add-from-recs"
          onClick={onAddRecs}
          className="bg-[#025979] hover:bg-[#024a63] text-white rounded-sm h-8 px-3 text-[11.5px] font-semibold inline-flex items-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2.25} />
          Add More from Recommendations
        </Button>
        <Button
          data-testid="selections-export"
          onClick={exportCsv}
          variant="outline"
          className="bg-white border-[#E5E7EB] hover:bg-[#F8FAFC] text-[#0F172A] rounded-sm h-8 px-3 text-[11.5px] font-semibold inline-flex items-center gap-1.5"
        >
          <Download className="w-3.5 h-3.5" strokeWidth={2.25} />
          Export Selections Summary
        </Button>
      </div>
    </div>
  );

  if (items.length === 0) {
    return (
      <div data-testid="tab-selections">
        {header}
        <Card className="p-10 text-center" data-testid="tab-selections-empty">
          <Layers className="w-9 h-9 mx-auto text-[#94A3B8]" strokeWidth={1.5} />
          <p
            className="mt-3 text-[15px] font-bold text-[#0F172A]"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            No initiatives selected yet
          </p>
          <p className="mt-1 text-[12.5px] text-[#64748B]">
            Open <span className="font-semibold">Recommendations</span> and add initiatives you want to pursue.
          </p>
          <Button
            onClick={onAddRecs}
            className="mt-3 bg-[#025979] hover:bg-[#024a63] text-white rounded-sm h-8 px-3 text-[12px] font-semibold"
            data-testid="selections-empty-goto-recs"
          >
            Browse Recommendations
          </Button>
        </Card>
      </div>
    );
  }

  // Stage transition: any value -> any value. When moving to On Hold, also
  // remember the prior stage so a subsequent move back is contextual.
  const handleStageChange = (s, nextStage) => {
    if (nextStage === s.stage) return;
    const patch = { stage: nextStage };
    if (nextStage === "on_hold") patch.previous_stage = s.stage;
    onStageChange(s.id, patch);
  };

  return (
    <div className="space-y-3" data-testid="tab-selections">
      {header}

      <Card className="p-3.5" data-testid="selections-pipeline-card">
        <PipelineFlow stageCounts={stageCounts} />
      </Card>

      <Card className="p-0 overflow-hidden" data-testid="selections-table-card">
        <div className="px-4 py-2.5 flex items-center justify-between border-b border-[#E5E7EB]">
          <h3
            className="text-[13px] font-bold text-[#0F172A] tracking-tight"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Initiatives
          </h3>
          <p className="text-[10.5px] text-[#64748B]">
            {total} initiative{total === 1 ? "" : "s"}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px] border-collapse">
            <thead>
              <tr className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
                {[
                  "Initiative", "Stage", "Execution", "Risk",
                  "ROI Impact", "Effort", "Timeline",
                  "Commercial Snapshot", "Action",
                ].map((h) => (
                  <th
                    key={h}
                    className="text-left py-2 px-3 text-[10px] font-semibold text-[#64748B] uppercase tracking-[0.06em] whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((s) => {
                const card = recIndex[s.recommendation_id] || {};
                const bi = card.business_impact || {};
                const io = card.implementation_overview || {};
                const tag = categoryTag(s, card);
                const tagTone = tag ? CAT_TONE[tag.tone] || CAT_TONE.platform : null;
                const cost = Number(s.estimated_cost) || Number(bi.total_investment) || Number(io.total_cost) || 0;
                const savings = Number(s.estimated_savings) || Number(bi.annual_savings) || 0;
                const payback = s.payback ?? bi.payback_months ?? null;
                const roiPct = bi.three_year_roi_pct;
                const impactLabel = deriveImpactLevel({ savings, payback_months: payback });
                const weeks = Number(io.total_weeks) || 0;
                const eff = deriveEffort(weeks);
                const name = resolveInitiativeName(s, card);
                const execStatusKey = deriveExecStatusKey(s, {});
                const execMeta = EXEC_STATUS_META[execStatusKey];
                const ownProgress = typeof s.progress_pct === "number" ? Number(s.progress_pct) : null;
                const riskMeta = s?.risk_severity ? RISK_SEVERITY_META[s.risk_severity] : null;
                const isExpanded = expandedExecId === s.id;
                const isActivityExpanded = expandedActivityId === s.id;
                const activityCount = Array.isArray(s.activity_log) ? s.activity_log.length : 0;
                return (
                  <Fragment key={s.id}>
                  <tr
                    className="border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]/60"
                    data-testid={`selection-row-${s.id}`}
                  >
                    <td className="py-2.5 px-3 align-top">
                      {onOpenRecommendation && s.recommendation_id ? (
                        <button
                          type="button"
                          onClick={() => onOpenRecommendation(s.recommendation_id)}
                          data-testid={`selection-name-${s.id}`}
                          title="View recommendation details"
                          className="text-left text-[12.5px] font-semibold text-[#0F172A] hover:text-[#025979] hover:underline underline-offset-2 transition-colors leading-tight"
                          style={{ fontFamily: "Chivo, sans-serif" }}
                        >
                          {name}
                        </button>
                      ) : (
                        <p
                          className="text-[12.5px] font-semibold text-[#0F172A] leading-tight"
                          style={{ fontFamily: "Chivo, sans-serif" }}
                          data-testid={`selection-name-${s.id}`}
                        >
                          {name}
                        </p>
                      )}
                      <p className="mt-0.5 text-[10.5px] text-[#94A3B8] leading-snug">
                        Based on selected recommendation
                      </p>
                      <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                        {tag && tagTone && (
                          <span
                            className="inline-flex items-center text-[9.5px] font-semibold rounded-sm px-1.5 py-0.5"
                            style={{ backgroundColor: tagTone.bg, color: tagTone.fg }}
                          >
                            {tag.label}
                          </span>
                        )}
                        {s.source_type && (
                          <span className="text-[10px] text-[#94A3B8]">
                            {humanize(s.source_type)}
                          </span>
                        )}
                      </div>
                      {s.notes && (
                        <p className="mt-1 text-[10.5px] text-[#64748B] leading-snug line-clamp-2">
                          <span className="font-semibold text-[#475569]">Latest Update:</span>{" "}
                          {s.notes}
                        </p>
                      )}
                    </td>
                    <td className="py-2.5 px-3 align-top">
                      <StageBadge stage={s.stage} />
                    </td>
                    <td className="py-2.5 px-3 align-top w-[160px]">
                      <span
                        className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold rounded-sm px-1.5 py-0.5"
                        style={{ backgroundColor: execMeta.bg, color: execMeta.fg }}
                        data-testid={`selection-exec-status-${s.id}`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: execMeta.dot }} />
                        {execMeta.label}
                      </span>
                      {(ownProgress != null && ownProgress > 0) && (
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <div className="flex-1 h-1.5 bg-[#F1F5F9] rounded-sm overflow-hidden">
                            <div
                              className="h-full rounded-sm"
                              style={{
                                width: `${Math.max(0, Math.min(100, ownProgress))}%`,
                                backgroundColor: execStatusKey === "at_risk" ? "#DC2626" : C.blue,
                              }}
                              data-testid={`selection-progress-${s.id}`}
                            />
                          </div>
                          <span className="text-[10px] font-semibold text-[#64748B] tabular-nums w-7 text-right">
                            {Math.round(ownProgress)}%
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 px-3 align-top w-[120px]">
                      {riskMeta ? (
                        <span
                          className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold rounded-sm px-1.5 py-0.5"
                          style={{ backgroundColor: riskMeta.bg, color: riskMeta.fg }}
                          title={s?.risk_description || ""}
                          data-testid={`selection-risk-${s.id}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: riskMeta.dot }} />
                          {riskMeta.label}
                        </span>
                      ) : (
                        <span className="text-[#94A3B8] text-[11.5px]">—</span>
                      )}
                      {s?.risk_description && (
                        <p className="mt-0.5 text-[10px] text-[#64748B] line-clamp-2" title={s.risk_description}>
                          {s.risk_description}
                        </p>
                      )}
                    </td>
                    <td className="py-2.5 px-3 align-top">
                      {roiPct != null ? (
                        <>
                          <p
                            className="text-[12.5px] font-bold text-[#16A34A] tabular-nums leading-tight"
                            style={{ fontFamily: "Chivo, sans-serif" }}
                          >
                            +{Math.round(Number(roiPct))}%
                          </p>
                          {impactLabel && (
                            <p className="text-[10px] text-[#64748B] mt-0.5">{impactLabel}</p>
                          )}
                        </>
                      ) : savings > 0 ? (
                        <>
                          <p
                            className="text-[12.5px] font-bold text-[#16A34A] tabular-nums leading-tight"
                            style={{ fontFamily: "Chivo, sans-serif" }}
                          >
                            {fmtMoney(savings)}
                          </p>
                          <p className="text-[10px] text-[#64748B] mt-0.5">Annual Savings</p>
                        </>
                      ) : (
                        <span className="text-[#94A3B8]">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 align-top">
                      {eff ? (
                        <span className="inline-flex items-center gap-1.5 text-[11.5px] text-[#0F172A]">
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: eff.dot }} />
                          {eff.label}
                        </span>
                      ) : (
                        <span className="text-[#94A3B8]">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 align-top">
                      {weeks > 0 ? (
                        <p
                          className="text-[11.5px] font-semibold text-[#0F172A] tabular-nums"
                          style={{ fontFamily: "Chivo, sans-serif" }}
                        >
                          {weeks} weeks
                        </p>
                      ) : (
                        <span className="text-[#94A3B8]">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 align-top">
                      {(cost > 0 || savings > 0 || payback != null) ? (
                        <div className="flex flex-col gap-0.5 text-[11px]">
                          <span className="text-[#0F172A] tabular-nums">
                            <span className="text-[#94A3B8] mr-1">Cost:</span>
                            {cost > 0 ? fmtMoney(cost) : "—"}
                          </span>
                          <span className="text-[#0F172A] tabular-nums">
                            <span className="text-[#94A3B8] mr-1">Savings:</span>
                            {savings > 0 ? `${fmtMoney(savings)}/yr` : "—"}
                          </span>
                          <span className="text-[#0F172A] tabular-nums">
                            <span className="text-[#94A3B8] mr-1">Payback:</span>
                            {payback != null ? `${fmtNum(payback)} mo` : "—"}
                          </span>
                        </div>
                      ) : (
                        <span className="text-[#94A3B8]">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 align-top">
                      <div className="flex items-center gap-1.5">
                        <StageDropdown
                          stage={s.stage}
                          onChange={(next) => handleStageChange(s, next)}
                          testid={`selection-stage-${s.id}`}
                          options={stageOptions}
                        />
                        {canEdit && onUpdateExecution && (
                          <button
                            type="button"
                            data-testid={`selection-edit-execution-${s.id}`}
                            onClick={() => {
                              setExpandedActivityId(null);
                              setExpandedExecId(isExpanded ? null : s.id);
                            }}
                            aria-expanded={isExpanded}
                            className={`text-[#475569] hover:text-[#025979] hover:bg-[#F1F5F9] rounded-sm p-1 transition-colors ${
                              isExpanded ? "bg-[#F1F5F9] text-[#025979]" : ""
                            }`}
                            title={isExpanded ? "Close execution editor" : "Edit execution details"}
                          >
                            <Gauge className="w-3.5 h-3.5" strokeWidth={2} />
                          </button>
                        )}
                        <button
                          type="button"
                          data-testid={`selection-activity-${s.id}`}
                          onClick={() => {
                            setExpandedExecId(null);
                            setExpandedActivityId(isActivityExpanded ? null : s.id);
                          }}
                          aria-expanded={isActivityExpanded}
                          className={`relative text-[#475569] hover:text-[#025979] hover:bg-[#F1F5F9] rounded-sm p-1 transition-colors ${
                            isActivityExpanded ? "bg-[#F1F5F9] text-[#025979]" : ""
                          }`}
                          title={isActivityExpanded ? "Close activity timeline" : "View activity timeline"}
                        >
                          <Clock3 className="w-3.5 h-3.5" strokeWidth={2} />
                          {activityCount > 0 && (
                            <span
                              className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 rounded-full bg-[#025979] text-white text-[8.5px] font-semibold leading-[14px] text-center tabular-nums"
                              data-testid={`selection-activity-count-${s.id}`}
                            >
                              {activityCount > 99 ? "99+" : activityCount}
                            </span>
                          )}
                        </button>
                        <button
                          type="button"
                          data-testid={`selection-remove-${s.id}`}
                          onClick={() => onRemove(s.id)}
                          className="text-[#94A3B8] hover:text-[#DC2626] hover:bg-[#FEE2E2] rounded-sm p-1 transition-colors"
                          aria-label="Remove selection"
                          title="Remove from selections"
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && canEdit && onUpdateExecution && (
                    <tr
                      className="bg-[#F8FAFC] border-b border-[#E5E7EB]"
                      data-testid={`selection-execution-editor-${s.id}`}
                    >
                      <td colSpan={9} className="py-3 px-3">
                        <ExecutionEditor
                          selection={s}
                          onSave={async (patch) => {
                            await onUpdateExecution(s.id, patch);
                            toast.success("Execution updated");
                            setExpandedExecId(null);
                          }}
                          onCancel={() => setExpandedExecId(null)}
                        />
                      </td>
                    </tr>
                  )}
                  {isActivityExpanded && (
                    <tr
                      className="bg-[#F8FAFC] border-b border-[#E5E7EB]"
                      data-testid={`selection-activity-panel-${s.id}`}
                    >
                      <td colSpan={9} className="py-3 px-4">
                        <div className="flex items-center justify-between mb-2">
                          <p
                            className="text-[11px] font-semibold uppercase tracking-[0.10em] text-[#64748B]"
                          >
                            Activity Timeline
                          </p>
                          <button
                            type="button"
                            onClick={() => setExpandedActivityId(null)}
                            data-testid={`selection-activity-close-${s.id}`}
                            className="text-[10.5px] font-semibold text-[#64748B] hover:text-[#025979]"
                          >
                            Close
                          </button>
                        </div>
                        <ActivityTimeline selection={s} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Tab 3 — Recommendations (Card-based decision UI)
//
// Modern decision-making layout: KPI summary at top, filter tabs + search,
// responsive 3-col card grid. Full original report is preserved inside a
// "View Details" dialog so no data is lost.
// ---------------------------------------------------------------------------
const REC_CATEGORY_META = {
  Platform: { Icon: Package, bg: "#E0F2FE", fg: "#025979" },
  Process: { Icon: Gauge, bg: "#FEF3C7", fg: "#92400E" },
  Product: { Icon: Briefcase, bg: "#DCFCE7", fg: "#166534" },
  "AI Insights": { Icon: Lightbulb, bg: "#F0EDF9", fg: "#5847B0" },
};

const REC_SOURCE_META = {
  evaluate: { label: "Evaluate", bg: "#F1F5F9", fg: "#475569" },
  improve: { label: "Improve", bg: "#FEF3C7", fg: "#92400E" },
  futuroot: { label: "AI Insight", bg: "#F0EDF9", fg: "#5847B0" },
  consultant_manual: { label: "Manual", bg: "#FEE2E2", fg: "#991B1B" },
};

function deriveRecCategory(c) {
  const head = String(c?.recommendation_id || "").split(":")[0].toLowerCase();
  if (c?.source_type === "futuroot") return "AI Insights";
  if (c?.category) {
    const v = String(c.category).toLowerCase();
    if (v.includes("ai")) return "AI Insights";
    if (v.includes("process")) return "Process";
    if (v.includes("product")) return "Product";
    return "Platform";
  }
  if (head === "process") return "Process";
  if (head === "product") return "Product";
  if (head === "ai" || head === "ai_insight") return "AI Insights";
  return "Platform";
}

function deriveWhyText(c) {
  if (c?.executive_summary) {
    const sent = String(c.executive_summary).split(/(?<=[.!?])\s+/)[0];
    return sent.length > 220 ? sent.slice(0, 217) + "…" : sent;
  }
  if (c?.deployment_reason) return c.deployment_reason;
  if (Array.isArray(c?.assumptions) && c.assumptions[0]) return c.assumptions[0];
  return "Recommended based on assessment scoring across capability gaps and value drivers.";
}

function deriveDescription(c) {
  const parts = [];
  if (c?.solution) parts.push(c.solution);
  if (c?.deployment) parts.push(c.deployment);
  if (parts.length) return parts.join(" · ");
  if (c?.strategy_mode_label) return c.strategy_mode_label;
  return c?.coverage_label || "Transformation initiative";
}

function OptionMetric({ label, value, testid }) {
  return (
    <div data-testid={testid} className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#64748B]">
        {label}
      </p>
      <p
        className="mt-0.5 text-base font-bold text-[#0F172A]"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        {value ?? "—"}
      </p>
    </div>
  );
}

function ProposalBlock({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <section data-testid={testid} className="pt-6 mt-6 border-t border-[#F1F5F9]">
      <div className="flex items-center gap-2 mb-4">
        {Icon && (
          <div
            className="shrink-0 w-7 h-7 rounded-sm flex items-center justify-center"
            style={{ backgroundColor: C.blueSoft, color: C.blue }}
          >
            <Icon className="w-4 h-4" strokeWidth={2.25} />
          </div>
        )}
        <div>
          <h4
            className="text-[15px] font-bold text-[#0F172A] tracking-tight"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {title}
          </h4>
          {subtitle && (
            <p className="text-[11px] text-[#64748B] leading-tight">{subtitle}</p>
          )}
        </div>
      </div>
      {children}
    </section>
  );
}

function RecKpiCard({ label, value, hint, accent = false, testid }) {
  return (
    <div
      data-testid={testid}
      className={`border rounded-sm p-3 flex flex-col justify-between min-h-[78px] ${
        accent ? "border-[#025979]/30 bg-[#F0F9FF]" : "border-[#E5E7EB] bg-white"
      }`}
    >
      <p className="text-[9.5px] font-semibold uppercase tracking-[0.10em] text-[#64748B]">
        {label}
      </p>
      <div>
        <p
          className={`text-[20px] font-bold leading-none tabular-nums ${
            accent ? "text-[#025979]" : "text-[#0F172A]"
          }`}
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {value}
        </p>
        {hint && (
          <p className="mt-1 text-[10.5px] text-[#64748B] truncate">{hint}</p>
        )}
      </div>
    </div>
  );
}

function deriveContextFit(c) {
  const drivers = c?.business_impact?.value_drivers || [];
  const risks = c?.risks || [];
  const best = [];
  drivers.slice(0, 2).forEach((d) => {
    if (d?.label) best.push(`Driving ${String(d.label).toLowerCase()}`);
  });
  if (c?.coverage_label) best.push(`${c.coverage_label} networks`);
  if (
    c?.strategy_mode_label &&
    !best.some((b) => b.toLowerCase().includes(String(c.strategy_mode_label).toLowerCase()))
  ) {
    best.push(`${c.strategy_mode_label} approach`);
  }
  const notIdeal = [];
  risks.slice(0, 2).forEach((r) => {
    const txt = typeof r === "string" ? r : r?.label || r?.name || "";
    if (txt) notIdeal.push(String(txt));
  });
  return {
    best: best.slice(0, 3).filter(Boolean),
    notIdeal: notIdeal.slice(0, 2).filter(Boolean),
  };
}

// Full original recommendation report (preserved verbatim, rendered inside
// the View Details dialog so existing data is not removed).
function RecFullReport({ c, alreadySelected, onAdd, insights, onViewAllInsights, onOpenInsight }) {
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [rosterOpen, setRosterOpen] = useState(false);
  const bi = c.business_impact || {};
  const io = c.implementation_overview || {};
  const plan = (c.implementation_plan || {}).groups || [];
  const rm = c.resource_model || {};
  const drivers = bi.value_drivers || [];
  const breakdown = c.warehouse_breakdown || [];
  const assumptions = c.assumptions || [];
  const risks = c.risks || [];
  const typeCount = c.warehouse_type_count || c.site_count || 0;
  const locationCount = c.warehouse_count_total || 0;
  const coverageLine = `${c.coverage_label} · ${typeCount} warehouse type${typeCount === 1 ? "" : "s"} · ${locationCount} location${locationCount === 1 ? "" : "s"}`;

  return (
    <div data-testid={`rec-detail-${c.id}`}>
      {/* HEADER */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Chip bg={C.blueSoft} fg={C.blue}>
              {c.strategy_mode_label || c.strategy_type_label || "Transformation"}
            </Chip>
            <span className="text-[11px] text-[#64748B]">· Coverage: {coverageLine}</span>
          </div>
          <h3
            className="text-xl font-bold text-[#0F172A] tracking-tight"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {c.title}
          </h3>
          {(c.solution || c.deployment) && (
            <div className="mt-2 flex flex-wrap items-baseline gap-x-5 gap-y-1">
              {c.solution && (
                <div>
                  <span className="text-[10px] uppercase tracking-[0.12em] text-[#64748B] mr-2">Solution</span>
                  <span className="text-sm font-semibold text-[#0F172A]">{c.solution}</span>
                </div>
              )}
              {c.deployment && (
                <div>
                  <span className="text-[10px] uppercase tracking-[0.12em] text-[#64748B] mr-2">Deployment</span>
                  <span className="text-sm font-semibold text-[#0F172A]">{c.deployment}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => onAdd(c)}
          disabled={alreadySelected}
          data-testid={`rec-detail-add-${c.id}`}
          className="h-9 px-4 text-xs bg-[#025979] hover:bg-[#024a63] text-white disabled:opacity-50 disabled:cursor-not-allowed rounded-sm font-semibold shrink-0"
        >
          {alreadySelected ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
              Added to Initiatives
            </>
          ) : (
            <>
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add to Initiatives
            </>
          )}
        </Button>
      </div>

      {/* QUICK KPI ROW — at-a-glance decision metrics */}
      <div
        className="mt-5 grid grid-cols-3 gap-3"
        data-testid={`rec-detail-quick-kpis-${c.id}`}
      >
        <div className="border border-[#E5E7EB] rounded-sm px-3 py-2.5 bg-white">
          <p className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[#64748B]">
            ROI Impact
          </p>
          <p
            className="mt-1 text-[18px] font-bold text-[#16A34A] tabular-nums leading-none"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {bi.three_year_roi_pct != null ? `+${Math.round(bi.three_year_roi_pct)}%` : "—"}
          </p>
          <p className="mt-1 text-[10.5px] text-[#64748B]">3-year ROI</p>
        </div>
        <div className="border border-[#E5E7EB] rounded-sm px-3 py-2.5 bg-white">
          <p className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[#64748B]">
            Payback Period
          </p>
          <p
            className="mt-1 text-[18px] font-bold text-[#0F172A] tabular-nums leading-none"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {bi.payback_months != null ? `${Number(bi.payback_months).toFixed(1)} mo` : "—"}
          </p>
          <p className="mt-1 text-[10.5px] text-[#64748B]">Time to break-even</p>
        </div>
        <div className="border border-[#E5E7EB] rounded-sm px-3 py-2.5 bg-white">
          <p className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[#64748B]">
            Timeline
          </p>
          <p
            className="mt-1 text-[18px] font-bold text-[#0F172A] tabular-nums leading-none"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {io.total_weeks ? `${io.total_weeks} weeks` : "—"}
          </p>
          <p className="mt-1 text-[10.5px] text-[#64748B]">
            {io.total_months ? `~${io.total_months} months delivery` : "Delivery duration"}
          </p>
        </div>
      </div>

      {/* EXECUTIVE SUMMARY */}
      {c.executive_summary && (
        <div className="mt-6 pt-6 border-t border-[#F1F5F9]">
          {(() => {
            const sentences = String(c.executive_summary)
              .split(/(?<=[.!?])\s+/)
              .map((s) => s.trim())
              .filter(Boolean);
            if (sentences.length <= 2) {
              return (
                <p className="text-[13.5px] text-[#374151] leading-relaxed">
                  {c.executive_summary}
                </p>
              );
            }
            return (
              <ul className="space-y-1.5 text-[13.5px] text-[#374151] leading-relaxed">
                {sentences.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="shrink-0 mt-1.5 w-1 h-1 rounded-full bg-[#94A3B8]" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            );
          })()}
        </div>
      )}

      {/* WHERE THIS FITS — small context block */}
      {(() => {
        const fit = deriveContextFit(c);
        if (fit.best.length === 0 && fit.notIdeal.length === 0) return null;
        return (
          <div
            className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3"
            data-testid={`rec-detail-fit-${c.id}`}
          >
            {fit.best.length > 0 && (
              <div className="rounded-sm border border-[#DCFCE7] bg-[#F0FDF4] px-3.5 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[#166534] mb-1.5">
                  Best suited for
                </p>
                <ul className="space-y-1 text-[12.5px] text-[#0F172A] leading-snug">
                  {fit.best.map((t, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="shrink-0 mt-1.5 w-1 h-1 rounded-full bg-[#16A34A]" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {fit.notIdeal.length > 0 && (
              <div className="rounded-sm border border-[#FEF3C7] bg-[#FFFBEB] px-3.5 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[#92400E] mb-1.5">
                  Not ideal for
                </p>
                <ul className="space-y-1 text-[12.5px] text-[#0F172A] leading-snug">
                  {fit.notIdeal.map((t, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="shrink-0 mt-1.5 w-1 h-1 rounded-full bg-[#F59E0B]" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })()}

      {/* INSIGHT DRIVERS — lightweight traceability to evaluation insights */}
      {(() => {
        const list = Array.isArray(insights?.insights) ? insights.insights : [];
        if (list.length === 0) return null;
        const top = list.slice(0, 5);
        const remaining = Math.max(0, list.length - top.length);
        return (
          <ProposalBlock icon={Lightbulb} title="Insight Drivers" testid={`rec-detail-insight-drivers-${c.id}`}>
            <p className="text-[12px] text-[#64748B] mb-2">
              This recommendation is informed by the following evaluation insights:
            </p>
            <ul className="space-y-1.5 text-[13px] text-[#0F172A] leading-snug">
              {top.map((ins, idx) => {
                const clickable = !!onOpenInsight && !!ins?.id;
                return (
                  <li key={ins.id || idx} className="flex gap-2">
                    <span className="shrink-0 mt-1.5 w-1 h-1 rounded-full bg-[#025979]" />
                    {clickable ? (
                      <button
                        type="button"
                        onClick={() => onOpenInsight(ins.id)}
                        data-testid={`rec-detail-insight-bullet-${ins.id}`}
                        className="text-left text-[#0F172A] hover:text-[#025979] hover:underline underline-offset-2 transition-colors"
                      >
                        {ins.title}
                      </button>
                    ) : (
                      <span>{ins.title}</span>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
              {remaining > 0 ? (
                <span className="text-[11.5px] text-[#64748B]">
                  +{remaining} more insight{remaining === 1 ? "" : "s"}
                </span>
              ) : (
                <span />
              )}
              {onViewAllInsights && (
                <Button
                  variant="outline"
                  onClick={() => onViewAllInsights("evaluate")}
                  data-testid={`rec-detail-view-all-insights-${c.id}`}
                  className="h-7 px-2.5 text-[11.5px] bg-white border-[#E5E7EB] hover:bg-[#F1F5F9] text-[#0F172A] rounded-sm font-semibold inline-flex items-center gap-1"
                >
                  <Compass className="w-3.5 h-3.5" strokeWidth={2.25} />
                  View All Insights
                </Button>
              )}
            </div>
          </ProposalBlock>
        );
      })()}

      {/* BUSINESS IMPACT & ROI */}
      <ProposalBlock icon={TrendingUp} title="Business Impact & ROI">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <OptionMetric label="Total Investment" value={fmtMoney(bi.total_investment, bi.currency)} />
          <OptionMetric label="Annual Savings" value={fmtMoney(bi.annual_savings, bi.currency)} />
          <OptionMetric
            label="Payback Period"
            value={bi.payback_months != null ? `${Number(bi.payback_months).toFixed(1)} months` : "—"}
          />
          <OptionMetric
            label="3-Year ROI"
            value={bi.three_year_roi_pct != null ? `${Math.round(bi.three_year_roi_pct)}%` : "—"}
          />
        </div>
        {drivers.length > 0 && (
          <div className="mt-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#64748B] mb-2">
              Top Value Drivers
            </p>
            <ul className="divide-y divide-[#F1F5F9] border border-[#E5E7EB] rounded-sm">
              {drivers.map((d, i) => (
                <li key={d.key || i} className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <span className="text-sm text-[#0F172A] flex-1 min-w-0 truncate">{d.label}</span>
                  <span className="text-xs font-semibold text-[#64748B] shrink-0 w-12 text-right">
                    {d.share_pct != null ? `${Math.round(d.share_pct)}%` : ""}
                  </span>
                  <span
                    className="text-sm font-bold shrink-0 w-28 text-right"
                    style={{ color: C.green, fontFamily: "Chivo, sans-serif" }}
                  >
                    {fmtMoney(d.amount, d.currency)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {assumptions.length > 0 && (
          <div className="mt-4 rounded-sm bg-[#F8FAFC] border border-[#E5E7EB] px-3 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#64748B] mb-1.5">
              Assumptions
            </p>
            <ul className="space-y-1 text-[12px] text-[#4B5563] leading-snug">
              {assumptions.map((t, i) => (
                <li key={i} className="flex gap-2">
                  <span className="shrink-0" aria-hidden="true">•</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </ProposalBlock>

      {/* IMPLEMENTATION OVERVIEW */}
      <ProposalBlock icon={Clock3} title="Implementation Overview">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <OptionMetric
            label="Total Duration"
            value={io.total_weeks ? `${io.total_weeks} weeks${io.total_months ? ` / ${io.total_months} months` : ""}` : "—"}
          />
          <OptionMetric
            label="Total Effort"
            value={io.total_effort_hours ? `${Number(io.total_effort_hours).toLocaleString()} hrs` : "—"}
          />
          <OptionMetric label="Total Cost" value={fmtMoney(io.total_cost, io.currency)} />
          <OptionMetric label="Avg. Team Size" value={io.avg_team_fte ? `${io.avg_team_fte} FTE` : "—"} />
        </div>
        {plan.length > 0 && (
          <div className="mt-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#64748B] mb-2">
              Implementation Plan
            </p>
            <div className="space-y-3">
              {plan.map((g, gi) => (
                <div key={g.id || gi} className="border border-[#E5E7EB] rounded-sm overflow-hidden">
                  <div className="flex items-center justify-between bg-[#F8FAFC] px-3 py-2 border-b border-[#E5E7EB]">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-5 h-5 rounded-sm text-[10px] font-bold flex items-center justify-center"
                        style={{ backgroundColor: C.blueSoft, color: C.blue }}
                      >
                        {gi + 1}
                      </span>
                      <span className="text-sm font-bold text-[#0F172A]">{g.name}</span>
                    </div>
                    <span className="text-[11px] font-semibold text-[#64748B]">{g.duration_weeks} weeks</span>
                  </div>
                  {(g.sub_phases || []).length > 0 && (
                    <ul className="divide-y divide-[#F1F5F9]">
                      {g.sub_phases.map((sp, si) => (
                        <li key={sp.id || si} className="flex items-center justify-between px-3 py-1.5">
                          <span className="text-[13px] text-[#374151]">{sp.name}</span>
                          <span className="text-[11px] text-[#64748B] font-medium">{sp.duration_weeks}w</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </ProposalBlock>

      {/* RESOURCE MODEL */}
      {(rm.total || 0) > 0 && (
        <ProposalBlock icon={Layers} title="Resource Model">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm mb-3">
            <span>
              <span className="text-[10px] uppercase tracking-wider text-[#64748B] mr-2">Total</span>
              <span className="font-semibold text-[#0F172A]">{rm.total} resources</span>
            </span>
            <span>
              <span className="text-[10px] uppercase tracking-wider text-[#64748B] mr-2">Onsite</span>
              <span className="font-semibold text-[#0F172A]">{rm.onsite_count}</span>
            </span>
            <span>
              <span className="text-[10px] uppercase tracking-wider text-[#64748B] mr-2">Offshore</span>
              <span className="font-semibold text-[#0F172A]">{rm.offshore_count}</span>
            </span>
          </div>
          <button
            type="button"
            onClick={() => setRosterOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#025979] hover:text-[#024a63]"
          >
            {rosterOpen ? "Hide roster" : "View roster"}
            <span aria-hidden="true">{rosterOpen ? "−" : "+"}</span>
          </button>
          {rosterOpen && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="border border-[#E5E7EB] rounded-sm p-3 bg-white">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#64748B] mb-2">
                  Onsite Leadership Team
                </p>
                {(rm.onsite || []).length === 0 ? (
                  <p className="text-[12px] text-[#9CA3AF] italic">No team members assigned</p>
                ) : (
                  <ul className="space-y-1 text-[13px] text-[#0F172A]">
                    {(rm.onsite || []).map((r, i) => (
                      <li key={i}>• {r.role}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="border border-[#E5E7EB] rounded-sm p-3 bg-white">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#64748B] mb-2">
                  Offshore Delivery Team
                </p>
                {(rm.offshore || []).length === 0 ? (
                  <p className="text-[12px] text-[#9CA3AF] italic">No team members assigned</p>
                ) : (
                  <ul className="space-y-1 text-[13px] text-[#0F172A]">
                    {(rm.offshore || []).map((r, i) => (
                      <li key={i}>• {r.role}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </ProposalBlock>
      )}

      {/* RISKS */}
      {risks.length > 0 && (
        <ProposalBlock icon={AlertTriangle} title="Risks & Considerations">
          <ul className="space-y-2">
            {risks.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#374151]">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#F59E0B] shrink-0" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </ProposalBlock>
      )}

      {/* WAREHOUSE BREAKDOWN */}
      {breakdown.length > 0 && (
        <div className="mt-5 pt-4 border-t border-[#F1F5F9]">
          <button
            type="button"
            onClick={() => setBreakdownOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#025979] hover:text-[#024a63]"
          >
            <Map className="w-3.5 h-3.5" strokeWidth={2} />
            {breakdownOpen ? "Hide Warehouse Breakdown" : "View Warehouse Breakdown"}
            <span aria-hidden="true">{breakdownOpen ? "−" : "+"}</span>
          </button>
          {breakdownOpen && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-[#E5E7EB]">
                    <th className="text-left py-2 px-2 text-[10px] font-semibold text-[#64748B] uppercase tracking-[0.08em]">Warehouse Type</th>
                    <th className="text-left py-2 px-2 text-[10px] font-semibold text-[#64748B] uppercase tracking-[0.08em]">Locations</th>
                    <th className="text-left py-2 px-2 text-[10px] font-semibold text-[#64748B] uppercase tracking-[0.08em]">Solution</th>
                    <th className="text-left py-2 px-2 text-[10px] font-semibold text-[#64748B] uppercase tracking-[0.08em]">Deployment</th>
                    <th className="text-left py-2 px-2 text-[10px] font-semibold text-[#64748B] uppercase tracking-[0.08em]">Key Business Drivers</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.map((b) => (
                    <tr key={b.warehouse_type_key} className="border-b border-[#F1F5F9] last:border-0 align-top">
                      <td className="py-2 px-2 font-semibold text-[#0F172A]">{b.warehouse_type}</td>
                      <td className="py-2 px-2 text-[#4B5563]">{b.location_count != null ? b.location_count : "—"}</td>
                      <td className="py-2 px-2 text-[#025979] font-semibold">{b.solution || "—"}</td>
                      <td className="py-2 px-2 text-[#0F172A]">
                        <span className="font-medium">{b.deployment_model || "—"}</span>
                        {b.deployment_reason && (
                          <p className="text-[11px] text-[#6B7280] mt-0.5 leading-snug">{b.deployment_reason}</p>
                        )}
                      </td>
                      <td className="py-2 px-2 text-[#4B5563] text-[12px]">
                        {(b.business_drivers || []).length > 0 ? (b.business_drivers || []).join(" · ") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RecommendationsTab({ recommendations, selections, onAdd, insights, onViewAllInsights, onOpenInsight }) {
  const cards = recommendations?.cards || [];
  const selectedRecIds = new Set((selections || []).map((s) => s.recommendation_id));
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("impact_desc");
  const [openId, setOpenId] = useState(null);

  const enriched = useMemo(
    () =>
      cards.map((c) => {
        const cat = deriveRecCategory(c);
        const sourceMeta = REC_SOURCE_META[c.source_type] || REC_SOURCE_META.evaluate;
        const weeks = Number(c.implementation_overview?.total_weeks) || 0;
        const eff = deriveEffort(weeks);
        const roiPct = Number(c.business_impact?.three_year_roi_pct) || 0;
        return { ...c, _cat: cat, _src: sourceMeta, _weeks: weeks, _eff: eff, _roi: roiPct };
      }),
    [cards]
  );

  const total = enriched.length;
  const highImpact = enriched.filter((c) => c._roi >= 100).length;
  const quickWins = enriched.filter((c) => c._weeks > 0 && c._weeks < 12).length;
  const mediumTerm = enriched.filter((c) => c._weeks >= 12 && c._weeks <= 24).length;
  const longTerm = enriched.filter((c) => c._weeks > 24).length;

  const totalRoi3yr = enriched.reduce(
    (acc, c) => acc + (Number(c.business_impact?.annual_savings) || 0) * 3,
    0
  );
  const totalInvestment = enriched.reduce(
    (acc, c) => acc + (Number(c.business_impact?.total_investment) || 0),
    0
  );
  const avgRoiPct = totalInvestment > 0 ? Math.round((totalRoi3yr / totalInvestment - 1) * 100) : 0;

  const filtered = useMemo(() => {
    const map = { platform: "Platform", process: "Process", product: "Product", ai: "AI Insights" };
    let list = enriched;
    if (filter !== "all") list = list.filter((c) => c._cat === map[filter]);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((c) =>
        `${c.title || ""} ${c.solution || ""} ${c.executive_summary || ""} ${c.deployment || ""}`
          .toLowerCase()
          .includes(q)
      );
    }
    const arr = [...list];
    arr.sort((a, b) => {
      if (sort === "impact_desc") return b._roi - a._roi;
      if (sort === "impact_asc") return a._roi - b._roi;
      if (sort === "timeline_asc") return (a._weeks || 9999) - (b._weeks || 9999);
      if (sort === "timeline_desc") return b._weeks - a._weeks;
      return 0;
    });
    return arr;
  }, [enriched, filter, search, sort]);

  const filterTabs = [
    { id: "all", label: "All", count: total },
    { id: "platform", label: "Platform", count: enriched.filter((c) => c._cat === "Platform").length },
    { id: "process", label: "Process", count: enriched.filter((c) => c._cat === "Process").length },
    { id: "product", label: "Product", count: enriched.filter((c) => c._cat === "Product").length },
    { id: "ai", label: "AI Insights", count: enriched.filter((c) => c._cat === "AI Insights").length },
  ];

  const openCard = enriched.find((c) => c.id === openId) || null;

  if (cards.length === 0) {
    return (
      <Card className="p-12 text-center" data-testid="tab-recommendations-empty">
        <Sparkles className="w-10 h-10 mx-auto text-[#94A3B8]" strokeWidth={1.5} />
        <p
          className="mt-4 text-base font-bold text-[#0F172A]"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          No recommendations available
        </p>
        <p className="mt-1 text-sm text-[#64748B]">
          Recommendations will appear once the evaluation is scored and released.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="tab-recommendations">
      {/* HEADER */}
      <div className="min-w-0">
        <h2
          className="text-[18px] font-bold text-[#0F172A] tracking-tight"
          style={{ fontFamily: "Chivo, sans-serif" }}
          data-testid="rec-page-title"
        >
          Recommendations
        </h2>
        <p className="mt-0.5 text-[12px] text-[#64748B] max-w-3xl">
          Explore transformation opportunities across platform, process, products, and AI-driven insights.
        </p>
      </div>

      {/* SUMMARY KPI ROW */}
      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 items-stretch"
        data-testid="rec-summary-row"
      >
        <RecKpiCard testid="rec-kpi-total" label="Total Opportunities" value={total} />
        <RecKpiCard testid="rec-kpi-high" label="High Impact" value={highImpact} hint=">100% 3-yr ROI" />
        <RecKpiCard testid="rec-kpi-quick" label="Quick Wins" value={quickWins} hint="< 12 weeks" />
        <RecKpiCard testid="rec-kpi-medium" label="Medium Term" value={mediumTerm} hint="12 - 24 weeks" />
        <RecKpiCard testid="rec-kpi-long" label="Long Term" value={longTerm} hint="> 24 weeks" />
        <RecKpiCard
          testid="rec-kpi-roi"
          label="ROI Potential (3 yrs)"
          value={avgRoiPct > 0 ? `+${avgRoiPct}%` : "—"}
          hint={totalRoi3yr > 0 ? fmtMoney(totalRoi3yr, "USD") : null}
          accent
        />
      </div>

      {/* FILTERS + SEARCH/SORT */}
      <div className="flex items-center gap-2 flex-wrap justify-between bg-white border border-[#E5E7EB] rounded-sm px-2 py-1.5">
        <div className="flex items-center gap-1 flex-wrap" data-testid="rec-filter-tabs">
          {filterTabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setFilter(t.id)}
              data-testid={`rec-filter-${t.id}`}
              className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-sm text-[11.5px] font-semibold transition-colors ${
                filter === t.id
                  ? "bg-[#0F172A] text-white"
                  : "text-[#475569] hover:bg-[#F1F5F9]"
              }`}
            >
              {t.label}
              <span
                className={`text-[10px] tabular-nums ${
                  filter === t.id ? "text-white/70" : "text-[#94A3B8]"
                }`}
              >
                {t.count}
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search recommendations..."
              data-testid="rec-search"
              className="h-7 pl-7 pr-2 text-[11.5px] border border-[#E5E7EB] rounded-sm w-56 focus:outline-none focus:border-[#025979]"
            />
          </div>
          <Button
            variant="outline"
            data-testid="rec-filter-button"
            className="h-7 px-2.5 text-[11.5px] bg-white border-[#E5E7EB] hover:bg-[#F1F5F9] text-[#0F172A] rounded-sm font-semibold inline-flex items-center gap-1"
            onClick={() => toast.info("Advanced filters coming soon")}
          >
            <Filter className="w-3.5 h-3.5" strokeWidth={2.25} />
            Filter
          </Button>
          <div className="relative">
            <SlidersHorizontal className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8] pointer-events-none" />
            <select
              data-testid="rec-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="h-7 pl-7 pr-7 text-[11.5px] border border-[#E5E7EB] bg-white rounded-sm font-semibold text-[#0F172A] focus:outline-none focus:border-[#025979] appearance-none"
            >
              <option value="impact_desc">Impact: High to Low</option>
              <option value="impact_asc">Impact: Low to High</option>
              <option value="timeline_asc">Timeline: Short to Long</option>
              <option value="timeline_desc">Timeline: Long to Short</option>
            </select>
            <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8] pointer-events-none" />
          </div>
        </div>
      </div>

      {/* CARD GRID */}
      {filtered.length === 0 ? (
        <Card className="p-10 text-center" data-testid="rec-empty-filter">
          <FileSearch className="w-9 h-9 mx-auto text-[#94A3B8]" strokeWidth={1.5} />
          <p
            className="mt-3 text-[14px] font-bold text-[#0F172A]"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            No matching recommendations
          </p>
          <p className="mt-1 text-[12px] text-[#64748B]">
            Try adjusting your search or filters.
          </p>
        </Card>
      ) : (
        <div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 items-stretch"
          data-testid="rec-grid"
        >
          {filtered.map((c) => {
            const alreadySelected = selectedRecIds.has(c.recommendation_id);
            const catMeta = REC_CATEGORY_META[c._cat] || REC_CATEGORY_META.Platform;
            const Icon = catMeta.Icon;
            const why = deriveWhyText(c);
            const desc = deriveDescription(c);

            return (
              <div
                key={c.id}
                className="bg-white border border-[#E5E7EB] rounded-sm p-3.5 flex flex-col hover:border-[#025979]/40 transition-colors"
                data-testid={`rec-card-${c.id}`}
              >
                {/* Header */}
                <div className="flex items-start gap-2.5">
                  <div
                    className="shrink-0 w-8 h-8 rounded-sm flex items-center justify-center"
                    style={{ backgroundColor: catMeta.bg, color: catMeta.fg }}
                  >
                    <Icon className="w-4 h-4" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3
                      className="text-[13.5px] font-bold text-[#0F172A] leading-tight tracking-tight line-clamp-2"
                      style={{ fontFamily: "Chivo, sans-serif" }}
                      data-testid={`rec-card-title-${c.id}`}
                    >
                      {c.title}
                    </h3>
                  </div>
                </div>

                {/* Tags */}
                <div
                  className="mt-2 flex items-center gap-1.5 flex-wrap"
                  data-testid={`rec-card-tags-${c.id}`}
                >
                  <span
                    className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10.5px] font-semibold"
                    style={{ backgroundColor: catMeta.bg, color: catMeta.fg }}
                  >
                    {c._cat}
                  </span>
                  <span
                    className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10.5px] font-semibold"
                    style={{ backgroundColor: c._src.bg, color: c._src.fg }}
                  >
                    {c._src.label}
                  </span>
                </div>

                {/* Description */}
                <p
                  className="mt-2 text-[12px] text-[#475569] leading-snug line-clamp-2"
                  data-testid={`rec-card-desc-${c.id}`}
                >
                  {desc}
                </p>

                {/* Why highlight */}
                <div
                  className="mt-2.5 rounded-sm bg-[#F8FAFC] border-l-2 border-[#025979] px-2.5 py-2"
                  data-testid={`rec-card-why-${c.id}`}
                >
                  <p className="text-[9.5px] font-semibold uppercase tracking-[0.10em] text-[#025979]">
                    Why this recommendation?
                  </p>
                  <p className="mt-0.5 text-[11.5px] text-[#0F172A] leading-snug line-clamp-3">
                    {why}
                  </p>
                </div>

                {/* KPI row */}
                <div
                  className="mt-2.5 grid grid-cols-3 gap-2"
                  data-testid={`rec-card-kpis-${c.id}`}
                >
                  <div className="border border-[#E5E7EB] rounded-sm px-2 py-1.5">
                    <p className="text-[9px] font-semibold uppercase tracking-[0.08em] text-[#64748B]">
                      ROI Impact
                    </p>
                    <p
                      className="mt-0.5 text-[13px] font-bold text-[#16A34A] tabular-nums"
                      style={{ fontFamily: "Chivo, sans-serif" }}
                    >
                      {c._roi > 0 ? `+${Math.round(c._roi)}%` : "—"}
                    </p>
                  </div>
                  <div className="border border-[#E5E7EB] rounded-sm px-2 py-1.5">
                    <p className="text-[9px] font-semibold uppercase tracking-[0.08em] text-[#64748B]">
                      Effort
                    </p>
                    <p
                      className="mt-0.5 text-[13px] font-bold text-[#0F172A] inline-flex items-center gap-1"
                      style={{ fontFamily: "Chivo, sans-serif" }}
                    >
                      {c._eff ? (
                        <>
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: c._eff.dot }}
                          />
                          {c._eff.label}
                        </>
                      ) : (
                        "—"
                      )}
                    </p>
                  </div>
                  <div className="border border-[#E5E7EB] rounded-sm px-2 py-1.5">
                    <p className="text-[9px] font-semibold uppercase tracking-[0.08em] text-[#64748B]">
                      Timeline
                    </p>
                    <p
                      className="mt-0.5 text-[13px] font-bold text-[#0F172A] tabular-nums"
                      style={{ fontFamily: "Chivo, sans-serif" }}
                    >
                      {c._weeks > 0 ? `${c._weeks}w` : "—"}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-auto pt-2.5 flex items-center gap-1.5">
                  <Button
                    onClick={() => onAdd(c)}
                    disabled={alreadySelected}
                    data-testid={`rec-card-add-${c.id}`}
                    className="flex-1 h-7 px-2 text-[11.5px] bg-[#025979] hover:bg-[#024a63] text-white disabled:opacity-50 disabled:cursor-not-allowed rounded-sm font-semibold inline-flex items-center justify-center gap-1"
                  >
                    {alreadySelected ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Added
                      </>
                    ) : (
                      <>
                        <Plus className="w-3.5 h-3.5" />
                        Add to Initiatives
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setOpenId(c.id)}
                    data-testid={`rec-card-view-${c.id}`}
                    className="h-7 px-2.5 text-[11.5px] bg-white border-[#E5E7EB] hover:bg-[#F1F5F9] text-[#0F172A] rounded-sm font-semibold"
                  >
                    View Details
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DETAILS DIALOG (preserves the full original report) */}
      <Dialog open={!!openCard} onOpenChange={(open) => !open && setOpenId(null)}>
        <DialogContent
          className="max-w-[80vw] w-[80vw] max-h-[88vh] overflow-y-auto p-0 sm:rounded-sm"
          data-testid="rec-details-dialog"
        >
          {openCard && (
            <>
              <DialogTitle className="sr-only">{openCard.title}</DialogTitle>
              <div className="p-7 sm:p-8">
                <RecFullReport
                  c={openCard}
                  alreadySelected={selectedRecIds.has(openCard.recommendation_id)}
                  onAdd={(card) => {
                    onAdd(card);
                  }}
                  insights={insights}
                  onViewAllInsights={(filter) => {
                    setOpenId(null);
                    onViewAllInsights?.(filter || "evaluate");
                  }}
                  onOpenInsight={(id) => {
                    setOpenId(null);
                    onOpenInsight?.(id);
                  }}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 4 — Insights (Card-based decision UI — mirrors Recommendations tab)
//
// Same layout, card style, filter bar, and details dialog as the
// Recommendations tab so the Client Dashboard reads as one consistent
// decision layer.
// ---------------------------------------------------------------------------
const INSIGHT_SEVERITY_META = {
  high: { label: "High", bg: "#FEE2E2", fg: "#991B1B", dot: "#DC2626", Icon: AlertTriangle },
  medium: { label: "Medium", bg: "#FEF3C7", fg: "#92400E", dot: "#F59E0B", Icon: BarChart3 },
  low: { label: "Low", bg: "#E0F2FE", fg: "#025979", dot: "#025979", Icon: CheckCircle2 },
};

const INSIGHT_SOURCE_META = {
  evaluate: { id: "evaluate", label: "Evaluate", bg: "#F1F5F9", fg: "#475569" },
  improve: { id: "improve", label: "Improve", bg: "#FEF3C7", fg: "#92400E" },
  ai: { id: "ai", label: "AI", bg: "#F0EDF9", fg: "#5847B0" },
};

function deriveInsightSource(i) {
  const tags = (i?.source_tags || []).map((t) => String(t).toLowerCase());
  if (tags.includes("futuroot") || tags.includes("ai") || tags.includes("ai_insight")) return "ai";
  if (tags.includes("improve")) return "improve";
  return "evaluate";
}

function humanizeSiteKey(key) {
  if (!key) return null;
  return String(key).replace(/[_-]+/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

function deriveInsightWhy(i) {
  // Backend now provides a structured `why_matters` per issue type — use it when present.
  if (i?.why_matters) return i.why_matters;
  const sev = String(i?.severity || "medium").toLowerCase();
  if (sev === "high")
    return "Materially impacts cost, throughput, or risk — addressing this should be prioritized.";
  if (sev === "low") return "Minor optimization opportunity worth tracking.";
  return "Slows process maturity and limits ROI realization across the network.";
}

function deriveInsightRootCause(i) {
  const ref = i?.supporting_ref || {};
  const kind = String(ref.kind || "").toLowerCase();
  if (kind === "section_score" && ref.value != null) {
    return `Maturity score of ${Number(ref.value).toFixed(2)}/4.0 is below the 2.0 baseline expected for this section.`;
  }
  if (kind === "pain_severity" && ref.value) {
    return `Client reported "${String(ref.value).replace(/_/g, " ")}" operational pain during the evaluation.`;
  }
  if (kind === "challenge_level" && ref.value) {
    return `Transformation challenge level reported as "${String(ref.value).replace(/_/g, " ")}".`;
  }
  if (kind === "site_tag") {
    return "Surfaced as a curated capability gap during the site-level evaluation.";
  }
  return null;
}

function deriveInsightBusinessImpact(i) {
  const sev = String(i?.severity || "medium").toLowerCase();
  const tags = (i?.source_tags || []).map((t) => String(t).toLowerCase());
  if (sev === "high") {
    if (tags.includes("client_input")) {
      return "Operational pain at this location is likely depressing throughput, increasing labor cost, and elevating execution risk.";
    }
    return "Left unaddressed, this gap is likely to compound across waves and reduce realized transformation value.";
  }
  if (sev === "low") {
    return "Modest improvement potential — useful as a quick-win during rollout planning.";
  }
  return "Limits process maturity in this area and slows downstream ROI realization.";
}

function deriveInsightArea(i) {
  // Prefer the new aggregated field from the backend (handles multi-site grouping).
  if (i?.affected_label) return i.affected_label;
  if (Array.isArray(i?.affected_locations) && i.affected_locations.length > 0) {
    return i.affected_locations.length === 1
      ? i.affected_locations[0]
      : `${i.affected_locations.length} warehouse locations`;
  }
  const ref = i?.supporting_ref || {};
  if (ref.site_key) return humanizeSiteKey(ref.site_key);
  if (ref.section) return humanizeSiteKey(String(ref.section).replace(/_score$/, ""));
  return null;
}

function deriveInsightWhyBullets(i) {
  // Issue-id keyed bullets with severity-aware fallback. Kept as a UI concern
  // so the structure is reusable for future Improve / AI insight sources
  // without changing the backend payload.
  const id = String(i?.id || "");
  if (id === "operational_pain") {
    return [
      "Reduces execution efficiency and increases unit cost",
      "Risks throughput shortfall against service-level commitments",
      "Compounds change-management cost during transformation",
    ];
  }
  if (id === "integration_complexity") {
    return [
      "Slows process execution due to fragile interfaces",
      "Increases operational and project delivery risk",
      "Limits scalability of new automation investments",
    ];
  }
  if (id === "automation_maturity") {
    return [
      "Limits productivity and increases manual dependency",
      "Caps achievable throughput and labor cost reduction",
      "Slows response to demand variability",
    ];
  }
  if (id === "transformation_challenge") {
    return [
      "Increases delivery risk and timeline pressure",
      "Demands stronger change-management investment",
    ];
  }
  if (id.startsWith("section_score:")) {
    return [
      "Slows process maturity and limits ROI realization",
      "Reduces leverage from downstream automation initiatives",
    ];
  }
  // Fallback: derive from severity when issue type is not pre-mapped
  const sev = String(i?.severity || "medium").toLowerCase();
  if (sev === "high") {
    return [
      "Materially impacts cost, throughput, or risk",
      "Should be prioritized in the transformation roadmap",
    ];
  }
  if (sev === "low") return ["Minor optimization opportunity worth tracking"];
  return [
    "Slows process maturity and limits ROI realization",
    "Reduces operational scalability across the network",
  ];
}

function InsightFullReport({ i }) {
  const sev = INSIGHT_SEVERITY_META[i.severity] || INSIGHT_SEVERITY_META.medium;
  const sourceMeta = INSIGHT_SOURCE_META[deriveInsightSource(i)];
  const businessImpact = i?.business_impact || deriveInsightBusinessImpact(i);
  const issueText =
    (i?.consultant_notes && String(i.consultant_notes).trim()) ||
    i?.description ||
    "No additional description provided.";
  const whyBullets = deriveInsightWhyBullets(i);
  const rootCausesList = Array.isArray(i?.root_causes) ? i.root_causes.filter(Boolean) : [];
  const rootCauseFallback = rootCausesList.length === 0 ? deriveInsightRootCause(i) : null;
  const evidence = Array.isArray(i?.evidence) ? i.evidence.filter(Boolean) : [];
  const locations = Array.isArray(i?.affected_locations) ? i.affected_locations : [];
  const [sourceContextOpen, setSourceContextOpen] = useState(false);

  return (
    <div data-testid={`insight-detail-${i.id}`}>
      {/* HEADER */}
      <div className="flex flex-wrap items-start gap-3">
        <div
          className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center"
          style={{ backgroundColor: sev.bg, color: sev.fg }}
        >
          <sev.Icon className="w-4 h-4" strokeWidth={2.25} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span
              className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10.5px] font-semibold"
              style={{ backgroundColor: sev.bg, color: sev.fg }}
            >
              {sev.label} Impact
            </span>
            <span
              className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10.5px] font-semibold"
              style={{ backgroundColor: sourceMeta.bg, color: sourceMeta.fg }}
            >
              {sourceMeta.label}
            </span>
          </div>
          <h3
            className="text-xl font-bold text-[#0F172A] tracking-tight"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {i.title}
          </h3>
          {i?.description && (
            <p className="mt-1 text-[12.5px] text-[#64748B] leading-snug line-clamp-1">
              {i.description}
            </p>
          )}
        </div>
      </div>

      {/* WHAT IS THE ISSUE */}
      <ProposalBlock icon={AlertTriangle} title="What is the issue">
        <p className="text-[13.5px] text-[#374151] leading-relaxed">{issueText}</p>
      </ProposalBlock>

      {/* WHY THIS MATTERS — bullet list of business implications */}
      <ProposalBlock icon={Lightbulb} title="Why this matters">
        <ul className="space-y-1.5 text-[13.5px] text-[#374151] leading-relaxed">
          {whyBullets.map((b, idx) => (
            <li key={idx} className="flex gap-2">
              <span className="shrink-0 mt-1.5 w-1 h-1 rounded-full bg-[#025979]" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </ProposalBlock>

      {/* ROOT CAUSE */}
      {(rootCausesList.length > 0 || rootCauseFallback) && (
        <ProposalBlock icon={FileSearch} title="Root cause">
          {rootCausesList.length > 0 ? (
            <ul className="space-y-1.5 text-[13.5px] text-[#374151] leading-relaxed">
              {rootCausesList.map((rc, idx) => (
                <li key={idx} className="flex gap-2">
                  <span className="shrink-0 mt-1.5 w-1 h-1 rounded-full bg-[#94A3B8]" />
                  <span>{rc}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[13.5px] text-[#374151] leading-relaxed">{rootCauseFallback}</p>
          )}
        </ProposalBlock>
      )}

      {/* BUSINESS IMPACT */}
      <ProposalBlock icon={TrendingUp} title="Business impact">
        <p className="text-[13.5px] text-[#374151] leading-relaxed">{businessImpact}</p>
      </ProposalBlock>

      {/* WHERE THIS OCCURS */}
      {(locations.length > 0 || deriveInsightArea(i)) && (
        <ProposalBlock icon={Map} title="Where this occurs">
          <div
            className="flex flex-wrap items-center gap-2"
            data-testid={`insight-detail-area-${i.id}`}
          >
            {locations.length > 0 ? (
              locations.map((loc, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-[12px] font-semibold whitespace-nowrap"
                  style={{ backgroundColor: C.blueSoft, color: C.blue }}
                >
                  <Map className="w-3.5 h-3.5" strokeWidth={2.25} />
                  {loc}
                </span>
              ))
            ) : (
              <span
                className="inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-[12px] font-semibold whitespace-nowrap"
                style={{ backgroundColor: C.blueSoft, color: C.blue }}
              >
                <Map className="w-3.5 h-3.5" strokeWidth={2.25} />
                {deriveInsightArea(i)}
              </span>
            )}
          </div>
        </ProposalBlock>
      )}

      {/* SOURCE CONTEXT — collapsible (default collapsed). Future-ready for
          Improve / AI insight payloads — they can populate the same Source +
          Context + Indicators block without UI changes. */}
      <section
        className="pt-6 mt-6 border-t border-[#F1F5F9]"
        data-testid={`insight-detail-source-context-${i.id}`}
      >
        <button
          type="button"
          onClick={() => setSourceContextOpen((v) => !v)}
          aria-expanded={sourceContextOpen}
          data-testid={`insight-detail-source-context-toggle-${i.id}`}
          className="w-full flex items-center justify-between gap-2 group"
        >
          <div className="flex items-center gap-2">
            <div
              className="shrink-0 w-7 h-7 rounded-sm flex items-center justify-center"
              style={{ backgroundColor: C.blueSoft, color: C.blue }}
            >
              <FileSearch className="w-4 h-4" strokeWidth={2.25} />
            </div>
            <h4
              className="text-[15px] font-bold text-[#0F172A] tracking-tight"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              Source Context
            </h4>
          </div>
          <ChevronDown
            className={`w-4 h-4 text-[#64748B] transition-transform ${
              sourceContextOpen ? "rotate-180" : ""
            }`}
            strokeWidth={2.25}
          />
        </button>
        {sourceContextOpen && (
          <div className="mt-4 rounded-sm bg-[#F8FAFC] border border-[#E5E7EB] px-4 py-4 sm:px-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div className="border border-[#E5E7EB] rounded-sm px-3 py-2 bg-white">
                <p className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[#64748B]">
                  Source
                </p>
                <p className="mt-1 text-[12.5px] font-semibold text-[#0F172A]">
                  {sourceMeta.label}
                </p>
              </div>
              <div className="border border-[#E5E7EB] rounded-sm px-3 py-2 bg-white">
                <p className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[#64748B]">
                  Context
                </p>
                <p className="mt-1 text-[12.5px] font-semibold text-[#0F172A]">
                  {i?.context || "Process Assessment"}
                </p>
              </div>
            </div>
            {evidence.length > 0 ? (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[#64748B] mb-1.5">
                  Supporting Indicators
                </p>
                <ul className="space-y-1 text-[12.5px] text-[#374151] leading-snug">
                  {evidence.map((ev, idx) => (
                    <li key={idx} className="flex gap-2">
                      <span className="shrink-0 mt-1.5 w-1 h-1 rounded-full bg-[#94A3B8]" />
                      <span>{ev}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-[12px] text-[#94A3B8] italic">
                No structured indicators captured.
              </p>
            )}
          </div>
        )}
      </section>

      {/* DATA EXPLORER LINK — only when present */}
      {i?.data_explorer_link && (
        <div className="mt-4">
          <a
            href={i.data_explorer_link}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#025979] hover:text-[#024a63]"
          >
            <Database className="w-3.5 h-3.5" strokeWidth={2.25} />
            View in Data Explorer
          </a>
        </div>
      )}

      {/* RELATED RECOMMENDATIONS — future-ready */}
      {i.related_recommendation_id && (
        <ProposalBlock icon={Sparkles} title="Related recommendations">
          <p className="text-[12.5px] text-[#64748B]">
            Linked to{" "}
            <span className="font-semibold text-[#0F172A]">
              {i.related_recommendation_id}
            </span>
            .
          </p>
        </ProposalBlock>
      )}
    </div>
  );
}

function InsightsTab({ insights, initialFilter, onFilterApplied }) {
  const items = insights?.insights || [];
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(initialFilter || "all");
  const [sort, setSort] = useState("impact_desc");
  const [openId, setOpenId] = useState(null);

  // When the parent navigates here with a target filter (e.g., from the
  // Recommendation popup's "View All Insights"), apply it once and clear.
  useEffect(() => {
    if (initialFilter) {
      setFilter(initialFilter);
      onFilterApplied?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilter]);

  const enriched = useMemo(
    () =>
      items.map((i) => {
        const sev = INSIGHT_SEVERITY_META[i.severity] || INSIGHT_SEVERITY_META.medium;
        const sourceId = deriveInsightSource(i);
        return { ...i, _sev: sev, _sourceId: sourceId, _source: INSIGHT_SOURCE_META[sourceId] };
      }),
    [items]
  );

  const filtered = useMemo(() => {
    let list = enriched;
    if (filter !== "all") list = list.filter((c) => c._sourceId === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((c) =>
        `${c.title || ""} ${c.description || ""}`.toLowerCase().includes(q)
      );
    }
    const arr = [...list];
    const sevOrder = { high: 3, medium: 2, low: 1 };
    arr.sort((a, b) => {
      const ar = sevOrder[a.severity] || 0;
      const br = sevOrder[b.severity] || 0;
      if (sort === "impact_desc") return br - ar;
      if (sort === "impact_asc") return ar - br;
      if (sort === "title_asc") return String(a.title || "").localeCompare(String(b.title || ""));
      if (sort === "title_desc") return String(b.title || "").localeCompare(String(a.title || ""));
      return 0;
    });
    return arr;
  }, [enriched, filter, search, sort]);

  const filterTabs = [
    { id: "all", label: "All", count: enriched.length },
    { id: "evaluate", label: "Evaluate", count: enriched.filter((c) => c._sourceId === "evaluate").length },
    { id: "improve", label: "Improve", count: enriched.filter((c) => c._sourceId === "improve").length },
    { id: "ai", label: "AI", count: enriched.filter((c) => c._sourceId === "ai").length },
  ];

  const openCard = enriched.find((c) => c.id === openId) || null;

  if (items.length === 0) {
    return (
      <Card className="p-12 text-center" data-testid="tab-insights-empty">
        <Compass className="w-10 h-10 mx-auto text-[#94A3B8]" strokeWidth={1.5} />
        <p
          className="mt-4 text-base font-bold text-[#0F172A]"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          No insights yet
        </p>
        <p className="mt-1 text-sm text-[#64748B]">
          Executive insights from your evaluation will surface here.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="tab-insights">
      {/* HEADER */}
      <div className="min-w-0">
        <h2
          className="text-[18px] font-bold text-[#0F172A] tracking-tight"
          style={{ fontFamily: "Chivo, sans-serif" }}
          data-testid="insights-page-title"
        >
          Insights
        </h2>
        <p className="mt-0.5 text-[12px] text-[#64748B] max-w-3xl">
          Key operational gaps and transformation drivers
        </p>
      </div>

      {/* FILTERS + SEARCH/SORT */}
      <div className="flex items-center gap-2 flex-wrap justify-between bg-white border border-[#E5E7EB] rounded-sm px-2 py-1.5">
        <div className="flex items-center gap-1 flex-wrap" data-testid="insights-filter-tabs">
          {filterTabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setFilter(t.id)}
              data-testid={`insights-filter-${t.id}`}
              className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-sm text-[11.5px] font-semibold transition-colors ${
                filter === t.id
                  ? "bg-[#0F172A] text-white"
                  : "text-[#475569] hover:bg-[#F1F5F9]"
              }`}
            >
              {t.label}
              <span
                className={`text-[10px] tabular-nums ${
                  filter === t.id ? "text-white/70" : "text-[#94A3B8]"
                }`}
              >
                {t.count}
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search insights..."
              data-testid="insights-search"
              className="h-7 pl-7 pr-2 text-[11.5px] border border-[#E5E7EB] rounded-sm w-56 focus:outline-none focus:border-[#025979]"
            />
          </div>
          <Button
            variant="outline"
            data-testid="insights-filter-button"
            className="h-7 px-2.5 text-[11.5px] bg-white border-[#E5E7EB] hover:bg-[#F1F5F9] text-[#0F172A] rounded-sm font-semibold inline-flex items-center gap-1"
            onClick={() => toast.info("Advanced filters (Impact, Source) coming soon")}
          >
            <Filter className="w-3.5 h-3.5" strokeWidth={2.25} />
            Filter
          </Button>
          <div className="relative">
            <SlidersHorizontal className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8] pointer-events-none" />
            <select
              data-testid="insights-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="h-7 pl-7 pr-7 text-[11.5px] border border-[#E5E7EB] bg-white rounded-sm font-semibold text-[#0F172A] focus:outline-none focus:border-[#025979] appearance-none"
            >
              <option value="impact_desc">Impact: High to Low</option>
              <option value="impact_asc">Impact: Low to High</option>
              <option value="title_asc">Title: A to Z</option>
              <option value="title_desc">Title: Z to A</option>
            </select>
            <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8] pointer-events-none" />
          </div>
        </div>
      </div>

      {/* CARD GRID */}
      {filtered.length === 0 ? (
        <Card className="p-10 text-center" data-testid="insights-empty-filter">
          <FileSearch className="w-9 h-9 mx-auto text-[#94A3B8]" strokeWidth={1.5} />
          <p
            className="mt-3 text-[14px] font-bold text-[#0F172A]"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            No matching insights
          </p>
          <p className="mt-1 text-[12px] text-[#64748B]">
            Try adjusting your search or filters.
          </p>
        </Card>
      ) : (
        <div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 items-stretch"
          data-testid="insights-grid"
        >
          {filtered.map((c) => {
            const Icon = c._sev.Icon;
            const why = deriveInsightWhy(c);
            const area = deriveInsightArea(c);

            return (
              <div
                key={c.id}
                className="bg-white border border-[#E5E7EB] rounded-sm p-3.5 flex flex-col hover:border-[#025979]/40 transition-colors"
                data-testid={`insight-card-${c.id}`}
              >
                {/* Header */}
                <div className="flex items-start gap-2.5">
                  <div
                    className="shrink-0 w-8 h-8 rounded-sm flex items-center justify-center"
                    style={{ backgroundColor: c._sev.bg, color: c._sev.fg }}
                  >
                    <Icon className="w-4 h-4" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3
                      className="text-[13.5px] font-bold text-[#0F172A] leading-tight tracking-tight line-clamp-2"
                      style={{ fontFamily: "Chivo, sans-serif" }}
                      data-testid={`insight-card-title-${c.id}`}
                    >
                      {c.title}
                    </h3>
                  </div>
                </div>

                {/* Tags */}
                <div
                  className="mt-2 flex items-center gap-1.5 flex-wrap"
                  data-testid={`insight-card-tags-${c.id}`}
                >
                  <span
                    className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10.5px] font-semibold"
                    style={{ backgroundColor: c._sev.bg, color: c._sev.fg }}
                  >
                    {c._sev.label}
                  </span>
                  <span
                    className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10.5px] font-semibold"
                    style={{ backgroundColor: c._source.bg, color: c._source.fg }}
                  >
                    {c._source.label}
                  </span>
                </div>

                {/* Description */}
                <p
                  className="mt-2 text-[12px] text-[#475569] leading-snug line-clamp-2"
                  data-testid={`insight-card-desc-${c.id}`}
                >
                  {c.description || "—"}
                </p>

                {/* Why highlight */}
                <div
                  className="mt-2.5 rounded-sm bg-[#F8FAFC] border-l-2 border-[#025979] px-2.5 py-2"
                  data-testid={`insight-card-why-${c.id}`}
                >
                  <p className="text-[9.5px] font-semibold uppercase tracking-[0.10em] text-[#025979]">
                    Why this matters
                  </p>
                  <p className="mt-0.5 text-[11.5px] text-[#0F172A] leading-snug line-clamp-3">
                    {why}
                  </p>
                </div>

                {/* KPI / Impact row */}
                <div
                  className="mt-2.5 grid grid-cols-2 gap-2"
                  data-testid={`insight-card-kpis-${c.id}`}
                >
                  <div className="border border-[#E5E7EB] rounded-sm px-2 py-1.5">
                    <p className="text-[9px] font-semibold uppercase tracking-[0.08em] text-[#64748B]">
                      Impact
                    </p>
                    <p
                      className="mt-0.5 text-[13px] font-bold inline-flex items-center gap-1"
                      style={{ fontFamily: "Chivo, sans-serif", color: c._sev.fg }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c._sev.dot }} />
                      {c._sev.label}
                    </p>
                  </div>
                  <div className="border border-[#E5E7EB] rounded-sm px-2 py-1.5 min-w-0">
                    <p className="text-[9px] font-semibold uppercase tracking-[0.08em] text-[#64748B]">
                      Affected Area
                    </p>
                    {(() => {
                      const locs = Array.isArray(c?.affected_locations) ? c.affected_locations : [];
                      if (locs.length >= 3) {
                        return (
                          <p
                            className="mt-0.5 text-[12px] font-bold text-[#0F172A] leading-snug"
                            style={{ fontFamily: "Chivo, sans-serif" }}
                            title={locs.join(", ")}
                          >
                            {locs[0]}{" "}
                            <span className="text-[#64748B] font-semibold">
                              +{locs.length - 1} more
                            </span>
                          </p>
                        );
                      }
                      return (
                        <p
                          className="mt-0.5 text-[12px] font-bold text-[#0F172A] leading-snug line-clamp-2"
                          style={{ fontFamily: "Chivo, sans-serif" }}
                          title={area || ""}
                        >
                          {area || "—"}
                        </p>
                      );
                    })()}
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-auto pt-2.5 flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    onClick={() => setOpenId(c.id)}
                    data-testid={`insight-card-view-${c.id}`}
                    className="flex-1 h-7 px-2.5 text-[11.5px] bg-white border-[#E5E7EB] hover:bg-[#F1F5F9] text-[#0F172A] rounded-sm font-semibold"
                  >
                    View Details
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DETAILS DIALOG (mirrors Recommendations popup) */}
      <Dialog open={!!openCard} onOpenChange={(open) => !open && setOpenId(null)}>
        <DialogContent
          className="max-w-[1000px] w-[min(96vw,1000px)] max-h-[88vh] overflow-y-auto p-0 sm:rounded-sm"
          data-testid="insight-details-dialog"
        >
          {openCard && (
            <>
              <DialogTitle className="sr-only">{openCard.title}</DialogTitle>
              <div className="px-6 py-6 sm:px-8 sm:py-7">
                <InsightFullReport i={openCard} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 5 — Data Explorer  (Evidence layer — raw assessment inputs)
//
// Renders the appendix-style inputs from the evaluation, grouped into
// business sections. No ROI / recommendations / execution content — that
// lives in the Transformation Program / Recommendations tabs.
// ---------------------------------------------------------------------------
function DEKVTable({ rows, testid }) {
  if (!rows?.length) {
    return <p className="text-sm text-[#64748B]">No inputs captured.</p>;
  }
  return (
    <div data-testid={testid} className="divide-y-0 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
      {rows.map((r) => (
        <div
          key={r.field_key || r.label}
          className="flex items-start justify-between gap-4 py-2 border-b border-[#F1F5F9] last:border-0"
        >
          <span className="text-xs text-[#64748B] leading-snug shrink-0 max-w-[60%]">
            {r.label}
          </span>
          <span className="text-sm text-[#0F172A] text-right font-semibold break-words max-w-[55%]">
            {r.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function DESection({ section, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const isPerWh = section.scope === "per_warehouse";
  const sitesOut = section.sites || [];
  const rows = section.rows || [];

  return (
    <Card className="overflow-hidden" data-testid={`de-section-${section.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`de-section-toggle-${section.id}`}
        className="w-full flex items-center justify-between gap-3 px-6 py-4 hover:bg-[#F8FAFC] transition-colors"
      >
        <div className="flex items-center gap-3 text-left">
          <div
            className="shrink-0 w-8 h-8 rounded-sm flex items-center justify-center"
            style={{ backgroundColor: C.blueSoft, color: C.blue }}
          >
            <Database className="w-4 h-4" strokeWidth={2.25} />
          </div>
          <div>
            <h3
              className="text-base font-bold text-[#0F172A] tracking-tight"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              {section.label}
            </h3>
            <p className="text-[11px] text-[#64748B]">
              {section.empty
                ? "No inputs captured"
                : isPerWh
                  ? `${sitesOut.length} warehouse${sitesOut.length === 1 ? "" : "s"}`
                  : `${rows.length} field${rows.length === 1 ? "" : "s"}`}
            </p>
          </div>
        </div>
        <span className="text-xs font-semibold text-[#64748B]" aria-hidden="true">
          {open ? "−" : "+"}
        </span>
      </button>
      {open && !section.empty && (
        <div
          className="px-6 pb-5 pt-4 border-t border-[#F1F5F9]"
          data-testid={`de-details-${section.id}`}
        >
          {isPerWh ? (
            <div className="space-y-4">
              {sitesOut.map((s) => (
                <div
                  key={s.site_key}
                  className="border border-[#E5E7EB] rounded-sm p-4 bg-[#F8FAFC]"
                  data-testid={`de-site-${section.id}-${s.site_key}`}
                >
                  <p
                    className="text-sm font-bold text-[#0F172A] mb-3"
                    style={{ fontFamily: "Chivo, sans-serif" }}
                  >
                    {s.site_label}
                  </p>
                  <DEKVTable rows={s.rows} />
                </div>
              ))}
            </div>
          ) : (
            <DEKVTable rows={rows} />
          )}
        </div>
      )}
      {open && section.empty && (
        <div className="px-6 pb-5 pt-2 border-t border-[#F1F5F9]">
          <p className="text-sm text-[#64748B] italic">No data captured for this section.</p>
        </div>
      )}
    </Card>
  );
}

function DataTab({ data }) {
  const sections = data?.sections || [];
  if (!sections.length) {
    return (
      <Card className="p-10 text-center" data-testid="tab-data-empty">
        <Database className="w-10 h-10 mx-auto text-[#94A3B8]" strokeWidth={1.5} />
        <p
          className="mt-4 text-base font-bold text-[#0F172A]"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          No appendix data available
        </p>
        <p className="mt-1 text-sm text-[#64748B]">
          Raw assessment inputs will appear here once captured.
        </p>
      </Card>
    );
  }
  return (
    <div className="space-y-3" data-testid="tab-data">
      <div
        className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] px-4 py-3 text-[12px] text-[#475569] leading-snug"
        data-testid="de-intro"
      >
        <span className="font-semibold text-[#0F172A]">Your inputs.</span>{" "}
        Exact responses captured during the evaluation — grouped by business
        area and warehouse. No interpretation added.
      </div>
      {sections.map((s, idx) => (
        <DESection key={s.id} section={s} defaultOpen={idx === 0} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function TransformationDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const previewId = searchParams.get("assessment_id") || null;

  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [release, setRelease] = useState(null);
  const [selections, setSelections] = useState([]);
  const [notReleased, setNotReleased] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("program");
  const [insightsInitialFilter, setInsightsInitialFilter] = useState(null);
  // Lifted dialog state — any tab can request opening either popup.
  const [globalInsightId, setGlobalInsightId] = useState(null);
  const [globalRecCardId, setGlobalRecCardId] = useState(null);
  const platformLogo = useProductLogo();

  const assessmentId = release?.assessment_id || null;

  // Load (or reload) the live selections list. Lives in a plain collection,
  // not in the snapshot, because the client interacts with it in real time.
  const loadSelections = useCallback(async (aid) => {
    if (!aid) return;
    try {
      const data = await listSelections(aid);
      setSelections(Array.isArray(data) ? data : []);
    } catch {
      setSelections([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = await adminMe();
        if (cancelled) return;
        setMe(user);
      } catch {
        navigate("/transformation");
        return;
      }
      try {
        const data = await getMyRelease(previewId);
        if (!cancelled) {
          setRelease(data);
          setNotReleased(false);
          if (data?.assessment_id) await loadSelections(data.assessment_id);
        }
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 404 || (status === 400 && !previewId)) {
          // No legacy Client Portal release for this user. If they are a
          // Client Access Management user (have grants), send them to their
          // My Evaluations dashboard instead of the legacy portal empty state.
          try {
            const { resolveClientLanding } = await import("../lib/clientAccess");
            const landing = await resolveClientLanding();
            if (!cancelled && landing) {
              navigate(landing, { replace: true });
              return;
            }
          } catch { /* fall back to legacy empty state */ }
          setNotReleased(true);
        } else {
          const d = err.response?.data?.detail;
          setError(typeof d === "string" ? d : "Unable to load your dashboard");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, previewId, loadSelections]);

  const handleLogout = useCallback(async () => {
    try { await adminLogout(); } catch { /* ignore */ }
    navigate("/transformation");
  }, [navigate]);

  // Decision pipeline mutations — optimistic toasts, re-fetch on success.
  const addSelection = useCallback(async (card) => {
    if (!assessmentId) return;
    try {
      await createSelection(assessmentId, {
        recommendation_id: card.recommendation_id,
        stage: "selected",
        source_type: card.source_type || card.source || "evaluate",
        notes: card.justification || "",
      });
      await loadSelections(assessmentId);
      toast.success("Added to Selections");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add to Selections");
    }
  }, [assessmentId, loadSelections]);

  const changeSelectionStage = useCallback(async (id, patch) => {
    // Accepts either a stage string ({"selected"}) or a partial patch
    // ({stage, previous_stage}) so the SelectionsTab can record the prior
    // stage when transitioning to On Hold (used by the Resume action).
    const body = typeof patch === "string" ? { stage: patch } : (patch || {});
    if (!body.stage) return;
    try {
      const updated = await updateSelection(id, body);
      // Merge the API response (which now carries the freshly appended
      // `activity_log` entry) back into local state so the timeline panel
      // reflects the change without a full reload.
      setSelections((prev) =>
        prev.map((s) => (s.id === id ? { ...s, ...body, ...updated } : s))
      );
      toast.success("Stage updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update stage");
    }
  }, []);

  const updateSelectionExecution = useCallback(async (id, patch) => {
    // Per-initiative execution-tracking patch (status / progress / risk /
    // notes). Same endpoint as stage updates — uses PUT /selections/{id}.
    const body = patch || {};
    if (Object.keys(body).length === 0) return;
    try {
      const updated = await updateSelection(id, body);
      setSelections((prev) =>
        prev.map((s) => (s.id === id ? { ...s, ...body, ...updated } : s))
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save changes");
      throw e;
    }
  }, []);

  const removeSelection = useCallback(async (id) => {
    try {
      await deleteSelection(id);
      setSelections((prev) => prev.filter((s) => s.id !== id));
      toast.success("Selection removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove selection");
    }
  }, []);

  const snapshot = release?.snapshot || null;
  const programClient = snapshot?.program?.client || {};
  const companyLabel = programClient.company_name || me?.name || "Your Company";
  const roleLabel = (me?.role || "").toLowerCase();
  const isPreview = roleLabel !== "client" && !!previewId;

  // Cross-tab popup handlers — render the global dialogs at the bottom of
  // this component so any tab (Program, Initiatives, Recommendations,
  // Insights, embedded popups) can drive the same shared popup instance.
  const openInsight = useCallback((id) => {
    if (!id) return;
    setGlobalRecCardId(null);
    setGlobalInsightId(id);
  }, []);
  const openRecommendation = useCallback((recIdOrCardId) => {
    if (!recIdOrCardId) return;
    const cards = (snapshot?.recommendations || {}).cards || [];
    const card =
      cards.find((c) => c.id === recIdOrCardId) ||
      cards.find((c) => c.recommendation_id === recIdOrCardId);
    if (!card) return;
    setGlobalInsightId(null);
    setGlobalRecCardId(card.id);
  }, [snapshot]);
  const gotoTab = useCallback((tabId) => setActiveTab(tabId), []);

  const insightsList = (snapshot?.insights || {}).insights || [];
  const recCardsList = (snapshot?.recommendations || {}).cards || [];
  const openInsightCard =
    globalInsightId ? insightsList.find((i) => i.id === globalInsightId) : null;
  const openRecCard =
    globalRecCardId ? recCardsList.find((c) => c.id === globalRecCardId) : null;
  const selectedRecIdsSet = new Set((selections || []).map((s) => s.recommendation_id));

  const content = useMemo(() => {
    if (!snapshot) return null;
    switch (activeTab) {
      case "program":
        return (
          <ProgramTab
            program={snapshot.program}
            selections={selections}
            insights={snapshot.insights}
            recommendations={snapshot.recommendations}
            assessmentId={release?.assessment_id}
            onGotoRecs={() => setActiveTab("recommendations")}
            onGotoInitiatives={() => setActiveTab("selections")}
            onGotoInsights={() => setActiveTab("insights")}
            onOpenInsight={openInsight}
            onOpenRecommendation={openRecommendation}
          />
        );
      case "selections":
        return (
          <SelectionsTab
            selections={selections}
            recommendations={snapshot?.recommendations}
            onStageChange={changeSelectionStage}
            onRemove={removeSelection}
            onAddRecs={() => setActiveTab("recommendations")}
            onOpenRecommendation={openRecommendation}
            onUpdateExecution={updateSelectionExecution}
            canEdit={roleLabel !== "client"}
          />
        );
      case "recommendations":
        return (
          <RecommendationsTab
            recommendations={snapshot.recommendations}
            selections={selections}
            onAdd={addSelection}
            insights={snapshot.insights}
            onViewAllInsights={(filter) => {
              setInsightsInitialFilter(filter || "evaluate");
              setActiveTab("insights");
            }}
            onOpenInsight={openInsight}
          />
        );
      case "insights":
        return (
          <InsightsTab
            insights={snapshot.insights}
            initialFilter={insightsInitialFilter}
            onFilterApplied={() => setInsightsInitialFilter(null)}
          />
        );
      case "data":
        // Data Explorer tab removed from Client Dashboard — redirect to
        // Insights so any deep-link or persisted URL/state still resolves
        // to a valid screen. Backend data + consultant-side logic untouched.
        return (
          <InsightsTab
            insights={snapshot.insights}
            initialFilter={insightsInitialFilter}
            onFilterApplied={() => setInsightsInitialFilter(null)}
          />
        );
      default: return null;
    }
  }, [snapshot, activeTab, selections, addSelection, changeSelectionStage, removeSelection, insightsInitialFilter, openInsight, openRecommendation, updateSelectionExecution, roleLabel]);

  const activeTabMeta = TABS.find((t) => t.id === activeTab) || TABS[0];

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Left sidebar — mirrors the Consultant workspace chrome so the client
          portal feels like part of the same product family. */}
      <aside
        data-testid="dashboard-sidebar"
        className="hidden md:flex w-56 shrink-0 flex-col bg-[#111827] text-white sticky top-0 h-screen"
      >
        <div className="px-4 py-4 border-b border-white/10">
          <button
            type="button"
            onClick={() => setActiveTab("program")}
            className="flex items-center gap-2 focus:outline-none"
            data-testid="sidebar-brand"
          >
            {platformLogo ? (
              <img loading="lazy"
                src={platformLogo}
                alt="Platform logo"
                className="h-8 w-auto object-contain"
                data-testid="sidebar-brand-logo"
              />
            ) : (
              <>
                <Briefcase className="w-4 h-4 text-white" strokeWidth={1.5} />
                <p
                  className="text-sm font-bold text-white"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                >
                  WarehouseX
                </p>
              </>
            )}
          </button>
          <p className="text-[10px] mt-1.5 text-white/70 truncate" data-testid="sidebar-user-email">
            {me?.email || ""}
          </p>
        </div>
        <nav className="flex-1 py-2 overflow-y-auto" data-testid="sidebar-nav">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`sidebar-tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                aria-current={active ? "page" : undefined}
                className={`w-full flex items-center gap-2.5 px-4 py-2 text-xs font-medium transition-colors ${
                  active
                    ? "bg-[#025979] text-white"
                    : "text-gray-400 hover:text-white hover:bg-gray-800"
                }`}
              >
                <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                <span className="truncate">{t.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-2" data-testid="sidebar-footer-nav">
          <p
            data-testid="sidebar-powered-by"
            className="text-[10px] leading-snug text-white/40 select-none"
          >
            Powered by PRIVA Operational Intelligence
          </p>
          <button
            data-testid="client-dashboard-logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-2 text-xs text-white/70 hover:text-white transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </aside>

      {/* Main column — top bar + content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Minimal top bar */}
        <header
          data-testid="dashboard-topbar"
          className="h-12 shrink-0 bg-white border-b border-[#E5E7EB] px-4 sm:px-6 flex items-center justify-between gap-3 sticky top-0 z-10"
        >
          <div className="min-w-0 flex items-center gap-3">
            {/* Mobile: tab dropdown */}
            <div className="md:hidden">
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                className="text-[12px] border border-[#E5E7EB] rounded-sm px-2 py-1"
                data-testid="dashboard-tabs-mobile"
              >
                {TABS.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="min-w-0 flex flex-col">
              <h1
                data-testid="dashboard-page-title"
                className="text-[14px] font-bold text-[#0F172A] tracking-tight truncate leading-tight"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                {activeTabMeta.label}
              </h1>
              {activeTabMeta.subtitle && (
                <p
                  data-testid="dashboard-page-subtitle"
                  className="mt-0.5 text-[11.5px] text-[#64748B] truncate leading-tight"
                >
                  {activeTabMeta.subtitle}
                </p>
              )}
            </div>
            {isPreview && (
              <Chip bg={C.amberSoft} fg="#92400E" testid="preview-chip">Preview</Chip>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="topbar-notifications"
              className="relative p-1.5 rounded-sm text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
              onClick={() => toast.info("No new notifications")}
              title="Notifications"
            >
              <Bell className="w-3.5 h-3.5" strokeWidth={2.25} />
            </button>
            <div
              className="flex items-center gap-2 pl-2 border-l border-[#E5E7EB]"
              data-testid="topbar-user"
            >
              <div className="w-6 h-6 rounded-full bg-[#0F172A] text-white flex items-center justify-center text-[10px] font-semibold">
                {(me?.name || me?.email || "C").slice(0, 1).toUpperCase()}
              </div>
              <div className="hidden sm:block leading-tight">
                <p className="text-[11px] font-semibold text-[#0F172A] truncate max-w-[140px]" data-testid="dashboard-user-email">
                  {me?.name || me?.email || "Client User"}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className="flex-1 overflow-auto">
          <div className="px-4 sm:px-6 py-4">
            {/* Page header — company + last-updated context. Same data the
                old top-of-page row carried; placed here so the inner content
                blocks remain untouched. */}
            <div className="mb-4 flex items-baseline gap-3 flex-wrap">
              <h2
                className="text-[18px] font-bold text-[#0F172A] tracking-tight"
                style={{ fontFamily: "Chivo, sans-serif" }}
                data-testid="client-dashboard-title"
              >
                {companyLabel}
              </h2>
              {release?.released_at && (
                <p className="text-[11px] text-[#64748B]">
                  · Last updated {fmtDate(release.released_at)}
                </p>
              )}
            </div>

            {loading ? (
              <Card className="p-12 text-center" data-testid="client-dashboard-loading">
                <p className="text-sm text-[#64748B]">Loading your dashboard…</p>
              </Card>
            ) : error ? (
              <Card className="p-10 text-center border-red-200 bg-red-50" data-testid="client-dashboard-error">
                <AlertTriangle className="w-8 h-8 mx-auto" style={{ color: C.red }} />
                <p className="mt-3 text-sm font-semibold text-[#991B1B]">{error}</p>
              </Card>
            ) : notReleased || !snapshot ? (
              <EmptyState onBack={() => navigate("/transformation")} />
            ) : (
              content
            )}
          </div>
        </main>
      </div>

      {/* Global popups — lifted to the parent so any tab can open either
          dialog. The same `InsightFullReport` and `RecFullReport` components
          render inside, preserving the existing UI exactly. */}
      <Dialog
        open={!!openInsightCard}
        onOpenChange={(open) => {
          if (!open) setGlobalInsightId(null);
        }}
      >
        <DialogContent
          className="max-w-[1000px] w-[min(96vw,1000px)] max-h-[88vh] overflow-y-auto p-0 sm:rounded-sm"
          data-testid="global-insight-details-dialog"
        >
          {openInsightCard && (
            <>
              <DialogTitle className="sr-only">{openInsightCard.title}</DialogTitle>
              <div className="px-6 py-6 sm:px-8 sm:py-7">
                <InsightFullReport i={openInsightCard} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!openRecCard}
        onOpenChange={(open) => {
          if (!open) setGlobalRecCardId(null);
        }}
      >
        <DialogContent
          className="max-w-[80vw] w-[80vw] max-h-[88vh] overflow-y-auto p-0 sm:rounded-sm"
          data-testid="global-rec-details-dialog"
        >
          {openRecCard && (
            <>
              <DialogTitle className="sr-only">{openRecCard.title}</DialogTitle>
              <div className="p-7 sm:p-8">
                <RecFullReport
                  c={openRecCard}
                  alreadySelected={selectedRecIdsSet.has(openRecCard.recommendation_id)}
                  onAdd={(card) => addSelection(card)}
                  insights={snapshot?.insights}
                  onViewAllInsights={(filter) => {
                    setGlobalRecCardId(null);
                    setInsightsInitialFilter(filter || "evaluate");
                    setActiveTab("insights");
                  }}
                  onOpenInsight={(id) => {
                    setGlobalRecCardId(null);
                    openInsight(id);
                  }}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
